import { z } from 'zod';

import {
  NON_STATUTE_TAIL,
  articleScopeTerms,
  authorityRefsFor,
  companionRefsFor,
  looksLikeTaxCodeSection,
  sectionChapter,
  uncitedRuleStrength,
} from './article-factcheck-authority';
import {
  THRESHOLD,
  isQuestion,
  isReportedHappening,
  speaksOfTheLaw,
  statesLegalRule,
  statesStrictLegalRule,
} from './article-factcheck-classify';
import { whyNotAClaim } from './article-factcheck-nonclaims';

export { inferDefaultCode } from './article-factcheck-authority';
export { hasLegalDutyCue } from './article-factcheck-classify';

/**
 * FACT-CHECK — verify an article's legal, statutory and numeric claims against
 * primary sources fetched for this check. Pure: no I/O, no model, no clock.
 *
 * It is NOT the editorial critique and NOT `claims_found_in_sources`. The
 * critique reads retained source text and judges prose; `claims_found_in_sources`
 * asks whether a section number or figure appears ANYWHERE in what research
 * read. This asks the narrower, harder question — does the cited statute
 * actually say what this sentence says it says — against text fetched fresh
 * from the legislature's own page, and it records its verdict beside the
 * article without ever changing the article.
 *
 * ── THE FIVE RULES THAT LIVE IN CODE, NOT IN THE PROMPT ──────────────────────
 * 1. Claims are extracted DETERMINISTICALLY (`extractFactCheckClaims`). The model
 *    never decides what is a claim, so it cannot skip an inconvenient one. That
 *    includes a legal rule the article states WITHOUT a citation (`uncited_rule`):
 *    a lexicon finds it and a static topic map (`article-factcheck-authority`)
 *    chooses which provision to read it against.
 * 2. A `supported`, `contradicted` or `misapplied` verdict is accepted only when
 *    its quote is an exact substring of the fetched excerpt
 *    (`resolveFactCheckClaims`). A quote the page does not contain downgrades the
 *    verdict to `unverifiable`.
 * 3. Applicability is code too. The judge reports `applies` for every verdict that
 *    rests on a quote; `misapplied` (the provision's scope does not reach what the
 *    article applies it to) can never be a pass, and an `unclear` scope can never
 *    be a pass either.
 * 4. The status is `factCheckStatus`: arithmetic over the verdicts, never a number
 *    or a boolean the model supplies. Only a claim that misstates the law FAILS the
 *    check; a claim the law does not state, or that could not be checked, asks for
 *    a human (`needs_review`).
 * 5. A record belongs to one article version. `readFactCheck` reports
 *    `isCurrent` by content hash, so an edited article never inherits a pass.
 */

/** What the model may answer for a claim it is shown. */
export const FACTCHECK_JUDGE_VERDICTS = [
  'supported',
  'contradicted',
  'unsupported',
  'unverifiable',
  'misapplied',
] as const;
/**
 * What a stored claim may carry. `not_applicable` is never the model's answer: it is what the code gives
 * an `event` claim, a statement of a date or a happening that states no legal rule (a letter's date, a
 * timeline row), so no statute can support or contradict it. It is left out of every count and of the gate.
 */
export const FACTCHECK_VERDICTS = [...FACTCHECK_JUDGE_VERDICTS, 'not_applicable'] as const;
export type FactCheckVerdict = (typeof FACTCHECK_VERDICTS)[number];
/**
 * `uncited_rule` — a sentence that states a legal rule and cites nothing. The
 * provisions it is judged against came from the checker's topic map, not from
 * the article.
 */
export type FactCheckClaimKind = 'statute' | 'figure' | 'deadline' | 'uncited_rule' | 'event';

export const FACTCHECK_APPLIES = ['applies', 'misapplied', 'unclear'] as const;
export type FactCheckApplies = (typeof FACTCHECK_APPLIES)[number];

/**
 * Event claims (dates and happenings that state no legal rule) are listed after the judged ones and fill
 * only what is left under the database's 40-claim ceiling, never more than this.
 */
export const MAX_EVENT_CLAIMS = 20;
export const MAX_FACTCHECK_CLAIMS_STORED = 40;

/** About thirty claims; statutory ones are kept first. */
export const MAX_FACTCHECK_CLAIMS = 30;
/** Uncited legal assertions are capped separately, so they never crowd out cited ones. */
export const MAX_UNCITED_RULE_CLAIMS = 8;
/** `finish_article_factcheck` refuses more than 40 claims. */
export const MAX_FACTCHECK_CLAIMS_TOTAL = MAX_FACTCHECK_CLAIMS + MAX_UNCITED_RULE_CLAIMS;
/** The most of one section's text kept on the stored record, for review. */
export const FACTCHECK_READ_EXCERPT_CHARS = 1500;
/** The stored record stays under this many characters; excerpts are shortened first. */
export const FACTCHECK_RECORD_MAX_CHARS = 40_000;
/** The per-job source-fetch quota enforced by `authorize_article_source_fetch`. */
export const MAX_FACTCHECK_SOURCE_FETCHES = 12;
/**
 * How much of a whole-chapter opening is shown to the judge, to the readiness check and to the critique, and
 * kept on the job. It was a 5,000-character prefix; the chapters the ITC articles cite as a whole open with a
 * definitions section longer than that, so the sections after it were never in front of anyone. One value,
 * read by every judge from the evidence store.
 */
export const FACTCHECK_EXCERPT_CHARS = 12_000;
/**
 * How much of one statute SECTION is shown to the judge and kept on the job. A cap of
 * 5,000 cut long sections (§ 41.43, § 151.359) before the subsection the article cites;
 * the judge then could not quote it and answered `unverifiable` where an earlier run,
 * with the text in view, had answered `contradicted`. A cap of 12,000 still cut § 41.45 (15,686
 * characters, read to subsection (g)(3)) and § 41.66 (13,465, read to (j-2)): the v4 run's "cut off before
 * subsection (h)/(k-1)". The longest section of the Texas codes the articles cite is about 16,000, so
 * 30,000 reads every section whole. A fetched statute window ends on a section boundary (`scan-article`).
 */
export const FACTCHECK_SECTION_EXCERPT_CHARS = 30_000;
/** The judge's sampling settings: as deterministic as the provider allows. */
export const FACTCHECK_JUDGE_TEMPERATURE = 0;
export const FACTCHECK_JUDGE_SEED = 20_261_003;
/**
 * Claims judged per model call. 30 claims is at most six calls. Eight per call made a reply of 6,000-8,000
 * tokens (thinking included) whose one over-long quote or reason sank the whole batch; five keeps a reply short
 * enough to be repaired or retried cheaply.
 */
export const FACTCHECK_JUDGE_BATCH = 5;
/** `usage_events.tool_name` for the judge's model calls; the job cost roll-up reads it. */
export const FACTCHECK_USAGE_TOOL_NAME = 'factcheck_article';
/** A quote shorter than this proves nothing: "the" is a substring of everything. */
export const MIN_QUOTE_CHARS = 12;

/**
 * The claim check is a release condition (founder decision, 2026-10-02): a new
 * external hand-off needs a CURRENT check whose status is `passed`,
 * `needs_review` or `nothing_to_check`. `article-release.ts` reads it through
 * `factCheckReleaseBlocker`; an article that already has a live publication for
 * its exact revision is exempt there (`externalHandoffGate`).
 */
export const FACTCHECK_RELEASE_GATE_ENABLED = true;

// ── Texas codes ────────────────────────────────────────────────────────────

interface CodeDef {
  code: string;
  label: string;
  names: readonly string[];
}

const TEXAS_CODES: readonly CodeDef[] = [
  { code: 'TX', label: 'Tax', names: ['tax'] },
  { code: 'GV', label: 'Government', names: ['government', "gov't", 'gov.'] },
  {
    code: 'LG',
    label: 'Local Government',
    names: ['local government', "local gov't", 'local gov.'],
  },
  {
    code: 'BC',
    label: 'Business & Commerce',
    names: ['business and commerce', 'business & commerce', 'bus. & com.', 'bus. & comm.'],
  },
  { code: 'BO', label: 'Business Organizations', names: ['business organizations', 'bus. orgs.'] },
  { code: 'UT', label: 'Utilities', names: ['utilities', 'util.'] },
  { code: 'NR', label: 'Natural Resources', names: ['natural resources', 'nat. res.'] },
  { code: 'PR', label: 'Property', names: ['property', 'prop.'] },
  { code: 'WA', label: 'Water', names: ['water'] },
  { code: 'ED', label: 'Education', names: ['education', 'educ.'] },
  { code: 'FI', label: 'Finance', names: ['finance', 'fin.'] },
  { code: 'IN', label: 'Insurance', names: ['insurance', 'ins.'] },
  { code: 'LA', label: 'Labor', names: ['labor'] },
  { code: 'PE', label: 'Penal', names: ['penal'] },
  {
    code: 'CP',
    label: 'Civil Practice & Remedies',
    names: ['civil practice and remedies', 'civil practice & remedies', 'civ. prac. & rem.'],
  },
  { code: 'HS', label: 'Health & Safety', names: ['health and safety', 'health & safety'] },
  { code: 'TN', label: 'Transportation', names: ['transportation', 'transp.'] },
  { code: 'OC', label: 'Occupations', names: ['occupations', 'occ.'] },
  { code: 'AG', label: 'Agriculture', names: ['agriculture', 'agric.'] },
  { code: 'HR', label: 'Human Resources', names: ['human resources', 'hum. res.'] },
  { code: 'EL', label: 'Election', names: ['election'] },
  { code: 'FA', label: 'Family', names: ['family', 'fam.'] },
  { code: 'ES', label: 'Estates', names: ['estates'] },
  { code: 'PW', label: 'Parks & Wildlife', names: ['parks and wildlife', 'parks & wildlife'] },
  { code: 'AL', label: 'Alcoholic Beverage', names: ['alcoholic beverage'] },
];

const CODE_BY_NAME = new Map<string, CodeDef>(
  TEXAS_CODES.flatMap((def) => def.names.map((name) => [name, def] as const)),
);
const CODE_LABEL = new Map<string, string>(
  TEXAS_CODES.map((def) => [def.code, def.label] as const),
);

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Longest names first so "Local Government" wins over "Government". */
const CODE_MENTION = new RegExp(
  `(?:\\bTex(?:as|\\.)?\\s+)?\\b(${[...CODE_BY_NAME.keys()]
    .sort((a, b) => b.length - a.length)
    .map((name) => escapeRegExp(name).replace(/ /g, '\\s+'))
    .join('|')})\\s+Code\\b`,
  'gi',
);

// ── Citations ──────────────────────────────────────────────────────────────

const SECTION_NUMBER = /\d+[A-Za-z]?(?:\.\d+[A-Za-z]?)+/g;
const SECTION_LIST = new RegExp(
  `(?:§§?|\\bSections?\\b|\\bSecs?\\.)\\s*((?:${SECTION_NUMBER.source})(?:\\s*(?:,|;|\\band\\b|&|\\bor\\b|\\bthrough\\b|[-–])\\s*(?:${SECTION_NUMBER.source}))*)`,
  'gi',
);
const CHAPTER_CITATION = /\bChapters?\s+(\d{2,4}[A-Za-z]?)\b/g;
/** What stands just before a `§` when the citation is to an agency rule or a federal code, not a Texas code. */
const NOT_A_TEXAS_CODE =
  /(?:\bAdministrative\s+Code|\bAdmin\.\s*Code|\bT\.?A\.?C\.?|\bC\.?F\.?R\.?|\bU\.?S\.?C\.?|\bTex\.?\s*Reg\.?|\bFed\.?\s*Reg\.?|\bInternal\s+Revenue\s+Code|\bI\.?R\.?C\.?)\s*$/i;
const LEGAL_WORD = /\b(?:code|statutes?|statutory|chapter|act|law)\b/i;

const MONEY = /\$\s?\d/;
const PERCENT = /\d(?:[\d.,]*)\s?(?:%|percent\b|per cent\b)/i;
const MONTH =
  '(?:Jan(?:uary)?|Feb(?:ruary)?|Mar(?:ch)?|Apr(?:il)?|May|June?|July?|Aug(?:ust)?|Sept?(?:ember)?|Oct(?:ober)?|Nov(?:ember)?|Dec(?:ember)?)\\.?';
/** A deadline is a date or a day count. The bare word "deadline" is not a claim. */
const DEADLINE = new RegExp(
  `\\b${MONTH}\\s+\\d{1,2}(?:st|nd|rd|th)?\\b|\\b(?:within|no later than|not later than|on or before|before|after|by)\\s+(?:the\\s+)?(?:\\w+\\s+){0,2}?\\d{1,3}[\\s-](?:calendar\\s+|business\\s+|working\\s+)?(?:days?|weeks?|months?|years?)\\b`,
  'i',
);

export interface StatuteRef {
  /** Stable id within one fact-check, shared by every claim that cites the same provision. */
  sourceId: string;
  /** Legislature code abbreviation (TX, GV...), or null when the sentence never names the code. */
  code: string | null;
  chapter: string;
  section: string | null;
  citation: string;
  url: string | null;
  /**
   * Set when the checker chose this provision, not the article: `authority` for the
   * topic map behind an uncited rule, `companion` for the neighbouring section a
   * compound claim also needs. Cited provisions are read first.
   */
  via?: 'authority' | 'companion';
}

export interface ExtractedClaim {
  id: string;
  kind: FactCheckClaimKind;
  /** The sentence as plain text. */
  text: string;
  refs: StatuteRef[];
  /** True when the refs came from the same paragraph, not from this sentence itself. */
  inherited: boolean;
  /** The claim's own paragraph and nearest heading, so the judge can see what it is applied to. */
  context?: string;
  /**
   * Set when the sentence passes on what a NON-statute page says (a Comptroller page, a county resolution, a
   * news report). It is judged against that page's text, never against a statute.
   */
  attribution?: ClaimAttribution;
}

export interface ClaimAttribution {
  /** Stable within one fact-check: `a1`, `a2`... Shared by every claim attributed to the same page. */
  sourceId: string;
  url: string;
  label: string;
}

export function statuteChapterUrl(code: string, chapter: string): string {
  return `https://statutes.capitol.texas.gov/Docs/${code}/htm/${code}.${chapter}.htm`;
}

export function statuteSectionUrl(code: string, chapter: string, section: string): string {
  return `${statuteChapterUrl(code, chapter)}#${section}`;
}

function citationLabel(code: string | null, chapter: string, section: string | null): string {
  const prefix = code ? `Texas ${CODE_LABEL.get(code) ?? code} Code ` : '';
  return section ? `${prefix}§ ${section}` : `${prefix}Chapter ${chapter}`;
}

// ── Text → sentences ───────────────────────────────────────────────────────

const ABBREVIATIONS = new Set([
  'sec',
  'secs',
  'tex',
  "gov't",
  'gov',
  'ann',
  'stat',
  'art',
  'ch',
  'chs',
  'subch',
  'no',
  'nos',
  'inc',
  'corp',
  'co',
  'ltd',
  'st',
  'vs',
  'etc',
  'dr',
  'mr',
  'mrs',
  'ms',
  'jr',
  'sr',
  'u.s',
  'e.g',
  'i.e',
  'bus',
  'com',
  'comm',
  'util',
  'nat',
  'res',
  'prop',
  'educ',
  'ins',
  'transp',
  'occ',
  'agric',
  'hum',
  'fam',
  'civ',
  'prac',
  'rem',
  'rev',
  'admin',
  'reg',
  'fed',
  'approx',
]);

function endsWithAbbreviation(sentence: string): boolean {
  const match = /([A-Za-z'.]+)\.$/.exec(sentence.trim());
  if (!match) return false;
  const token = match[1]!.toLowerCase();
  return ABBREVIATIONS.has(token) || /^[a-z]$/.test(token);
}

export function splitSentences(text: string): string[] {
  const pieces = text
    .split(/(?<=[.!?]["”')\]]?)\s+(?=["“'([]?[A-Z0-9$§])/)
    .map((piece) => piece.trim())
    .filter(Boolean);
  const merged: string[] = [];
  for (const piece of pieces) {
    const previous = merged[merged.length - 1];
    if (previous !== undefined && endsWithAbbreviation(previous))
      merged[merged.length - 1] = `${previous} ${piece}`;
    else merged.push(piece);
  }
  return merged;
}

function plainText(markdown: string): string {
  return markdown
    .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/<[^>]+>/g, ' ')
    .replace(/[`*_~]+/g, '')
    .replace(/[’‘]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/\u00A0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** A hyperlink in the article: where it points and the words that carry it. */
interface ArticleLink {
  href: string;
  text: string;
}

const MARKDOWN_LINK = /(?<!!)\[([^\]]*)\]\(\s*<?([^)\s>]+)[^)]*\)/g;

function linksIn(raw: string): ArticleLink[] {
  const found: ArticleLink[] = [];
  for (const match of raw.matchAll(MARKDOWN_LINK)) {
    const href = match[2]!;
    if (!/^https?:\/\//i.test(href)) continue;
    found.push({ href, text: plainText(match[1] ?? '') });
  }
  return found;
}

/** Statute pages and their mirrors: a link to one of them is a citation, never a source the sentence is attributed to. */
const STATUTE_HOST =
  /(?:^|\.)(?:statutes\.capitol\.texas\.gov|texas\.public\.law|law\.justia\.com)$/i;

export function isStatuteLink(href: string): boolean {
  try {
    return STATUTE_HOST.test(new URL(href).hostname);
  } catch {
    return false;
  }
}

interface Paragraph {
  units: string[];
  /** Aligned with `units`: true for a table row. A row's citation belongs to that row alone. */
  rows: boolean[];
  /** Aligned with `units`: true for a table's header row (the row above its `|---|` rule). A header names columns; it asserts nothing. */
  headers: boolean[];
  /** Aligned with `units`: the non-statute pages each sentence links to. */
  links: ArticleLink[][];
  /** A heading names the code in force for what follows, but never becomes a claim. */
  heading?: boolean;
  /** The nearest heading above this paragraph. */
  under?: string;
  /** Which heading section the paragraph sits in; a citation anywhere in it is "near" this paragraph. */
  section: number;
}

/** "Source:", "Sources:", "References:" — a footer that lists where things came from. */
const SOURCE_LINE = /^[\s>*_-]*(?:sources?|references?|further reading|citations?)\s*:/i;
const SOURCE_HEADING = /^(?:sources?|references?|further reading|citations?|bibliography)\s*:?$/i;

/** Gives each link to the first sentence at or after the previous one that carries its words. */
function linksBySentence(
  sentences: readonly string[],
  links: readonly ArticleLink[],
): ArticleLink[][] {
  const out: ArticleLink[][] = sentences.map(() => []);
  let cursor = 0;
  for (const link of links) {
    if (isStatuteLink(link.href)) continue;
    let index = link.text
      ? sentences.findIndex((sentence, i) => i >= cursor && sentence.includes(link.text))
      : -1;
    if (index < 0) index = Math.min(cursor, sentences.length - 1);
    if (index < 0) continue;
    out[index]!.push(link);
    cursor = index;
  }
  return out;
}

function paragraphsOf(markdown: string): Paragraph[] {
  const withoutFences = markdown.replace(/```[\s\S]*?```/g, '\n\n');
  const paragraphs: Paragraph[] = [];
  let under = '';
  let inSources = false;
  let section = 0;
  for (const block of withoutFences.split(/\n\s*\n/)) {
    const lines = block.split('\n').filter((line) => line.trim());
    if (!lines.length) continue;
    const units: string[] = [];
    const rows: boolean[] = [];
    const headers: boolean[] = [];
    const links: ArticleLink[][] = [];
    const prose: string[] = [];
    const flush = () => {
      if (prose.length) {
        const raw = prose.join(' ');
        const sentences = splitSentences(plainText(raw));
        units.push(...sentences);
        rows.push(...sentences.map(() => false));
        headers.push(...sentences.map(() => false));
        links.push(...linksBySentence(sentences, linksIn(raw)));
      }
      prose.length = 0;
    };
    const emit = () => {
      if (units.length)
        paragraphs.push({
          units: units.splice(0),
          rows: rows.splice(0),
          headers: headers.splice(0),
          links: links.splice(0),
          under,
          section,
        });
    };
    for (const line of lines) {
      if (/^\s*#{1,6}\s/.test(line)) {
        flush();
        emit();
        const title = plainText(line.replace(/^\s*#{1,6}\s+/, ''));
        inSources = SOURCE_HEADING.test(title);
        under = title;
        section += 1;
        if (!inSources)
          paragraphs.push({
            units: [title],
            rows: [false],
            headers: [false],
            links: [[]],
            heading: true,
            section,
          });
        continue;
      }
      // A source list is where claims came from, not a claim. It runs to the end of its block.
      if (inSources || SOURCE_LINE.test(line)) {
        flush();
        emit();
        break;
      }
      if (/^\s*\|/.test(line)) {
        flush();
        if (/^\s*\|?[\s:|-]+\|?\s*$/.test(line)) {
          // The row just above a table's rule is its header.
          const above = units.length - 1;
          if (above >= 0 && rows[above]) headers[above] = true;
          continue;
        }
        const row = plainText(line.replace(/\|/g, ' ; ')).replace(/^;\s*|\s*;$/g, '');
        if (row) {
          units.push(row);
          rows.push(true);
          headers.push(false);
          links.push(linksIn(line).filter((link) => !isStatuteLink(link.href)));
        }
        continue;
      }
      if (/^\s*(?:[-*+]|\d+[.)])\s+/.test(line)) {
        flush();
        prose.push(line.replace(/^\s*(?:[-*+]|\d+[.)])\s+/, ''));
        flush();
        continue;
      }
      prose.push(line.replace(/^\s*>\s?/, ''));
    }
    flush();
    emit();
  }
  return paragraphs;
}

// ── Claim extraction ───────────────────────────────────────────────────────

interface CodeMention {
  code: string;
  index: number;
}

function codeMentions(sentence: string): CodeMention[] {
  const found: CodeMention[] = [];
  for (const match of sentence.matchAll(CODE_MENTION)) {
    const def = CODE_BY_NAME.get(match[1]!.toLowerCase().replace(/\s+/g, ' '));
    if (def) found.push({ code: def.code, index: match.index! });
  }
  return found;
}

function nearestCode(mentions: readonly CodeMention[], index: number): string | null {
  let best: CodeMention | null = null;
  for (const mention of mentions) {
    if (
      !best ||
      Math.abs(mention.index - index) < Math.abs(best.index - index) ||
      (Math.abs(mention.index - index) === Math.abs(best.index - index) && mention.index < index)
    )
      best = mention;
  }
  return best?.code ?? null;
}

interface RawRef {
  code: string | null;
  chapter: string;
  section: string | null;
  via?: 'authority' | 'companion';
}

/**
 * The code an article ties to a statute number somewhere, so a bare mention elsewhere means the same code.
 * "Government Code § 403.602" in one sentence and "does the project fit a § 403.602 category" in another are
 * one section: the second was read as Tax Code § 403.602 (a page that is only navigation) because a Tax Code
 * citation stood near it, and the claim could never be read. A link to the legislature's page ties a number
 * to its code just as firmly (`/Docs/GV/htm/GV.403.htm#403.602`). A number tied to two codes is tied to none.
 */
export interface CodeBindings {
  bySection: ReadonlyMap<string, string | null>;
  byChapter: ReadonlyMap<string, string | null>;
  /**
   * Section numbers the article cites as an AGENCY RULE or a federal provision ("16 TAC § 25.194",
   * "26 C.F.R. § 1.482-1"), wherever the number appears. A bare "§ 25.194" three paragraphs later is the same
   * rule: it is never read as a section of any Texas code. The value is the page the article links the rule
   * to (a Texas Register notice, a PUC order), or null when it links nothing.
   */
  rules: ReadonlyMap<string, RuleBinding>;
}

export interface RuleBinding {
  url: string | null;
  label: string;
}

const NO_BINDINGS: CodeBindings = {
  bySection: new Map(),
  byChapter: new Map(),
  rules: new Map(),
};

const STATUTE_PAGE_URL =
  /\/Docs\/([A-Z]{2})\/htm\/\1\.(\d+[A-Za-z]?)\.htm(?:#(\d+[A-Za-z]?(?:\.\d+[A-Za-z]?)+))?/g;

/**
 * Ties each statute number to a code. A link to the legislature's page for that exact number is the
 * strongest evidence there is, and it outranks a code NAME seen near the number in a sentence: "Eligible
 * property sits in a zone under Tax Code Chapter 311 or 312 or an enterprise zone (§ 403.602(9))" names the
 * Tax Code beside a number whose own link says Government Code, and read by nearness it became Tax Code
 * § 403.602, a chapter that does not exist. A name still binds a number that carries no link. A number tied
 * to two codes by the same kind of evidence is tied to none.
 */
export function bindCodes(input: {
  markdown: string;
  urls?: readonly string[];
  units: readonly string[];
}): CodeBindings {
  const bind = (map: Map<string, string | null>, key: string, code: string) => {
    const existing = map.get(key);
    if (existing === undefined) map.set(key, code);
    else if (existing !== code) map.set(key, null);
  };
  const known = new Set(TEXAS_CODES.map((def) => def.code));
  const linkSection = new Map<string, string | null>();
  const linkChapter = new Map<string, string | null>();
  const nameSection = new Map<string, string | null>();
  const nameChapter = new Map<string, string | null>();
  for (const source of [input.markdown, ...(input.urls ?? [])])
    for (const match of source.matchAll(STATUTE_PAGE_URL)) {
      const code = match[1]!.toUpperCase();
      if (!known.has(code)) continue;
      bind(linkChapter, match[2]!.toUpperCase(), code);
      if (match[3]) bind(linkSection, match[3], code);
    }
  const rules = new Map<string, RuleBinding>();
  for (const link of linksIn(input.markdown))
    for (const match of link.text.matchAll(SECTION_LIST)) {
      if (!NOT_A_TEXAS_CODE.test(link.text.slice(Math.max(0, match.index! - 48), match.index!)))
        continue;
      for (const number of match[1]!.matchAll(SECTION_NUMBER))
        rules.set(number[0], { url: link.href, label: link.text });
    }
  for (const unit of input.units) {
    for (const match of unit.matchAll(SECTION_LIST)) {
      if (!NOT_A_TEXAS_CODE.test(unit.slice(Math.max(0, match.index! - 48), match.index!)))
        continue;
      for (const number of match[1]!.matchAll(SECTION_NUMBER))
        if (!rules.has(number[0])) rules.set(number[0], { url: null, label: match[0] });
    }
    const found = codeMentions(unit);
    if (!found.length) continue;
    for (const match of unit.matchAll(SECTION_LIST)) {
      const named = nearestCode(found, match.index!);
      if (!named) continue;
      for (const number of match[1]!.matchAll(SECTION_NUMBER)) {
        bind(nameSection, number[0], named);
        bind(nameChapter, sectionChapter(number[0]).toUpperCase(), named);
      }
    }
  }
  const merge = (
    linked: ReadonlyMap<string, string | null>,
    named: ReadonlyMap<string, string | null>,
  ) => {
    const merged = new Map<string, string | null>(named);
    for (const [key, code] of linked) if (code) merged.set(key, code);
    for (const [key, code] of linked) if (!code && !merged.has(key)) merged.set(key, null);
    return merged;
  };
  return {
    bySection: merge(linkSection, nameSection),
    byChapter: merge(linkChapter, nameChapter),
    rules,
  };
}

/**
 * `carried` is a code named earlier in the document. It may stand in for a
 * missing one only while it is fresh (named in this paragraph or the heading
 * above it) or when the citation is unmistakably statutory (§, or a word of law).
 * Otherwise "Section 3.2 of the lease" three pages after a Tax Code heading
 * would be read as a Tax Code section.
 *
 * `defaultCode` is the code an article about Texas property tax means by a bare
 * "Section 23.011", "Sec. 41.43" or "§ 22.28". It is used only for a number
 * shaped like a Tax Code section (chapter 1–313, two or more decimal digits) and
 * never for "Section 3.2 of the lease".
 */
function refsIn(
  sentence: string,
  carried: {
    code: string | null;
    fresh: boolean;
    defaultCode: string | null;
    bound?: CodeBindings;
  },
): RawRef[] {
  const bound = carried.bound ?? NO_BINDINGS;
  const boundCode = (number: string): string | null =>
    bound.bySection.get(number) ??
    bound.byChapter.get(sectionChapter(number).toUpperCase()) ??
    null;
  const mentions = codeMentions(sentence);
  const refs: RawRef[] = [];
  const seen = new Set<string>();
  const add = (ref: RawRef) => {
    const key = `${ref.code ?? '?'}|${ref.chapter}|${ref.section ?? ''}`;
    if (seen.has(key)) return;
    seen.add(key);
    refs.push(ref);
  };
  for (const match of sentence.matchAll(SECTION_LIST)) {
    // "16 Texas Administrative Code § 25.194" and "26 C.F.R. § 1.482-1" are rules of an agency, not sections of
    // a Texas code: reading the number as one fetched a chapter that does not exist.
    if (NOT_A_TEXAS_CODE.test(sentence.slice(Math.max(0, match.index! - 48), match.index!)))
      continue;
    const named = nearestCode(mentions, match.index!);
    // A number the article cites elsewhere as an agency rule is that rule here too, unless a Texas code is
    // named directly in front of it ("Utilities Code § 25.194"): a code mentioned somewhere in the sentence
    // does not make the number one of its sections.
    const directlyNamed = /\bCode\s*$/i.test(
      sentence.slice(Math.max(0, match.index! - 8), match.index!),
    );
    const numbers = [...match[1]!.matchAll(SECTION_NUMBER)]
      .map((number) => number[0])
      .filter((number) => directlyNamed || !bound.rules.has(number));
    if (!numbers.length) continue;
    const taxShaped = numbers.every(looksLikeTaxCodeSection);
    const notStatuteContext = NON_STATUTE_TAIL.test(sentence.slice(match.index! + match[0].length));
    let code = named ?? carried.code;
    const anchored = Boolean(named) || numbers.every((number) => bound.bySection.get(number));
    // "Section 3.2 of the lease" is not a statute. Without a code named in the
    // sentence, it must be fresh, carry a §, or at least speak the language of law.
    if (!anchored && !carried.fresh && !match[0].includes('§') && !LEGAL_WORD.test(sentence)) {
      if (!carried.defaultCode || !taxShaped || notStatuteContext) continue;
      code = carried.defaultCode;
    } else if (!code && carried.defaultCode && taxShaped && !notStatuteContext) {
      code = carried.defaultCode;
    }
    for (const number of numbers)
      add({
        code: bound.bySection.get(number) ?? named ?? boundCode(number) ?? code,
        chapter: sectionChapter(number),
        section: number,
      });
  }
  for (const match of sentence.matchAll(CHAPTER_CITATION)) {
    const code = nearestCode(mentions, match.index!) ?? (carried.fresh ? carried.code : null);
    if (!code) continue;
    add({ code, chapter: match[1]!.toUpperCase(), section: null });
  }
  return refs;
}

function sentenceKind(sentence: string): FactCheckClaimKind | null {
  if (MONEY.test(sentence) || PERCENT.test(sentence) || THRESHOLD.test(sentence)) return 'figure';
  if (DEADLINE.test(sentence)) return 'deadline';
  return null;
}

const KIND_ORDER: Record<FactCheckClaimKind, number> = {
  statute: 0,
  uncited_rule: 1,
  figure: 2,
  deadline: 3,
  event: 4,
};

export interface ClaimExtraction {
  claims: ExtractedClaim[];
  /** Claims found beyond the caps, so the report can say it did not look at them. */
  omitted: number;
  /** Date-and-event statements beyond the room left for them. They were never going to be judged. */
  eventsOmitted: number;
  /** The article's title (or opening sentence): what the article is about. */
  subject: string;
  /** The kinds of property the article discusses, so the judge can see what a provision is applied to. */
  scopeTerms: string[];
}

export interface ExtractOptions {
  maxClaims?: number;
  maxUncitedRules?: number;
  /**
   * The code a bare section number belongs to in this article, from
   * `inferDefaultCode`. Also what turns on uncited-rule extraction: the authority
   * map is Texas property tax only.
   */
  defaultCode?: string | null;
  /**
   * Pages the article names beyond the links in its text (an import's declared sources). A sentence that
   * names one of them ("The Comptroller page says ...") is attributed to that page.
   */
  sources?: readonly { url: string; title: string }[];
}

function articleSubject(markdown: string): string {
  const title = /^\s*#\s+(.+)$/m.exec(markdown)?.[1];
  if (title) return plainText(title).slice(0, 200);
  const first = paragraphsOf(markdown).find((paragraph) => !paragraph.heading);
  return (first?.units[0] ?? '').slice(0, 200);
}

// ── Attribution: whose words is this sentence reporting? ──────────────────

/** A page a sentence can be attributed to, with the names the article uses for it. */
interface KnownSource {
  url: string;
  label: string;
  aliases: string[];
}

const ALIAS_STOP = new Set([
  'texas',
  'office',
  'public',
  'accounts',
  'state',
  'county',
  'court',
  'commissioners',
  'department',
  'news',
  'times',
  'the',
  'of',
  'and',
  'board',
  'review',
  'council',
  'commission',
  'committee',
  'authority',
  'agency',
  'bureau',
  'city',
  'office',
  'national',
  'association',
  'institute',
  'center',
  'group',
  'report',
  'impact',
  'community',
  'radio',
  'public',
]);

/** Verbs and nouns that say a sentence is passing a source's words on. */
const ATTRIBUTION_WORDS =
  '(?:says?|said|states?|stated|reports?|reported|describes?|described|notes?|noted|explains?|explained|lays? out|laid out|wrote|writes|published|posted|guidelines|resolution|page|letter|release)';
const ATTRIBUTION_CUE = new RegExp(`\\b(?:${ATTRIBUTION_WORDS}|according to)\\b`, 'i');

export function normalizePageUrl(url: string): string {
  try {
    const parsed = new URL(url);
    const path = parsed.pathname.replace(/\/+$/, '');
    return `${parsed.hostname.toLowerCase().replace(/^www\./, '')}${path}${parsed.search}`;
  } catch {
    return url.trim().toLowerCase();
  }
}

function aliasesFor(title: string, url: string): string[] {
  const prefix = title
    .split(/[:–—]/)[0]!
    .replace(/\([^)]*\)/g, ' ')
    .replace(/,.*$/, '')
    .replace(/\s+/g, ' ')
    .trim();
  const aliases = new Set<string>();
  if (prefix.length >= 3 && prefix.length <= 60) aliases.add(prefix);
  const shorter = prefix.replace(/^(?:Office of the\s+|Office of\s+)?(?:Texas\s+)?/i, '').trim();
  if (shorter.length >= 3) aliases.add(shorter);
  for (const acronym of title.matchAll(/\(([A-Z]{2,6})\)/g)) aliases.add(acronym[1]!);
  for (const word of prefix.split(/\s+/))
    if (/^[A-Z][A-Za-z]{3,}$/.test(word) && !ALIAS_STOP.has(word.toLowerCase())) aliases.add(word);
  const county = /^([A-Z][a-z]+ (?:County|City))\b/.exec(prefix);
  if (county) aliases.add(county[1]!);
  try {
    const label = new URL(url).hostname.replace(/^www\./, '').split('.')[0] ?? '';
    if (label.length >= 3 && label.length <= 4) aliases.add(label.toUpperCase());
    else if (label.length >= 5) aliases.add(label[0]!.toUpperCase() + label.slice(1));
  } catch {
    // an address that does not parse has no host name to borrow
  }
  return [...aliases];
}

/** The alias is named beside a verb of reporting ("The Comptroller page says", "according to the Comptroller"). */
function mentions(sentence: string, alias: string): boolean {
  const escaped = escapeRegExp(alias).replace(/ /g, '\\s+');
  const named = new RegExp(`\\b${escaped}\\b`, /\s/.test(alias) ? 'gi' : 'g');
  const cueAfter = new RegExp(`^[^.;:]{0,50}?\\b${ATTRIBUTION_WORDS}\\b`, 'i');
  const cueBefore = /\baccording to\s+(?:the\s+)?$/i;
  for (const match of sentence.matchAll(named)) {
    const end = match.index! + match[0].length;
    if (cueAfter.test(sentence.slice(end)) || cueBefore.test(sentence.slice(0, match.index!)))
      return true;
  }
  return false;
}

function knownSourcesOf(
  markdown: string,
  declared: readonly { url: string; title: string }[] | undefined,
): KnownSource[] {
  const byKey = new Map<string, KnownSource>();
  const add = (url: string, title: string) => {
    if (isStatuteLink(url)) return;
    const key = normalizePageUrl(url);
    const existing = byKey.get(key);
    if (existing) {
      for (const alias of aliasesFor(title, url))
        if (!existing.aliases.includes(alias)) existing.aliases.push(alias);
      return;
    }
    byKey.set(key, { url, label: title, aliases: aliasesFor(title, url) });
  };
  for (const source of declared ?? []) add(source.url, source.title);
  for (const link of linksIn(markdown)) add(link.href, link.text || link.href);
  return [...byKey.values()];
}

interface RawAttribution {
  url: string;
  label: string;
}

/** The page a sentence is attributed to: one it links to, then one it names beside a verb of reporting. */
function attributionOf(
  sentence: string,
  ownLinks: readonly ArticleLink[],
  known: readonly KnownSource[],
): RawAttribution | null {
  const link = ownLinks.find((candidate) => !isStatuteLink(candidate.href));
  if (link) {
    const source = known.find((item) => normalizePageUrl(item.url) === normalizePageUrl(link.href));
    return { url: link.href, label: source?.label ?? (link.text || link.href) };
  }
  if (!ATTRIBUTION_CUE.test(sentence)) return null;
  const named = known.find((item) => item.aliases.some((alias) => mentions(sentence, alias)));
  return named ? { url: named.url, label: named.label } : null;
}

/** A sentence led by a pronoun or a short name for the page the sentence before it reported. */
const PAGE_CONTINUATION =
  /^\s*(?:it|they|the\s+(?:page|guidance|guidelines|resolution|faq|form|order|notice))\b[^.;:]{0,60}?\b(?:also\s+)?(?:says?|states?|defines?|requires?|lists?|explains?|notes?|adds?|provides?|describes?|puts|sets|points?|directs?)\b/i;

/**
 * The page an agency rule is linked to, when the sentence cites that rule by number ("(§ 25.194(d)(2))") and
 * names no Texas code beside it. The rule is not in the legislature's statutes, so the sentence is judged
 * against the page the article itself links the rule to.
 */
function ruleAttributionOf(sentence: string, bound: CodeBindings): RawAttribution | null {
  if (!bound.rules.size) return null;
  for (const match of sentence.matchAll(SECTION_LIST)) {
    if (
      nearestCode(codeMentions(sentence), match.index!) &&
      /\bCode\s*$/i.test(sentence.slice(Math.max(0, match.index! - 8), match.index!))
    )
      continue;
    for (const number of match[1]!.matchAll(SECTION_NUMBER)) {
      const rule = bound.rules.get(number[0]);
      if (rule?.url) return { url: rule.url, label: rule.label };
    }
  }
  return null;
}

/**
 * Statute citations first, then uncited legal rules, dollar and percentage
 * figures, then deadlines. One claim per sentence. A figure or deadline sentence
 * with no citation of its own inherits the provisions its paragraph cites, so
 * "the penalty is 10 percent" is judged against the section the previous
 * sentence named. A sentence that states a legal rule and cites nothing at all
 * ("the burden falls on the taxpayer") becomes an `uncited_rule`, judged against
 * the provisions the topic map names for it.
 *
 * A sentence that states a legal rule (a duty, a threshold, a deadline, a duration or an eligibility
 * rule, see `statesLegalRule`) is a legal claim even with no citation of its own: it takes the provisions
 * its paragraph cites, or the ones the topic map names, or the nearest citation in the same section. A
 * sentence that REPORTS a date or a happening is an `event` and is never judged against a statute.
 *
 * A sentence that passes on what a NON-statute page says (a Comptroller page, a county resolution, a news
 * report) is attributed to that page and judged against the page's own text, never against a statute.
 */
export function extractFactCheckClaims(
  markdown: string,
  options: ExtractOptions | number = {},
): ClaimExtraction {
  const opts: ExtractOptions = typeof options === 'number' ? { maxClaims: options } : options;
  const maxClaims = opts.maxClaims ?? MAX_FACTCHECK_CLAIMS;
  const maxUncited = opts.maxUncitedRules ?? MAX_UNCITED_RULE_CLAIMS;
  const defaultCode = opts.defaultCode ?? null;
  const known = knownSourcesOf(markdown, opts.sources);
  const paragraphs = paragraphsOf(markdown);
  let carriedCode: string | null = null;
  let codeFresh = false;
  interface Own {
    unit: string;
    refs: RawRef[];
    row: boolean;
    header: boolean;
    links: ArticleLink[];
  }
  interface Scanned {
    paragraph: Paragraph;
    own: Own[];
    /** Provisions this paragraph's own prose cites (a table row's citation belongs to that row alone). */
    refs: RawRef[];
  }
  const scanned: Scanned[] = [];
  const bound = bindCodes({
    markdown,
    urls: (opts.sources ?? []).map((source) => source.url),
    units: paragraphs.flatMap((paragraph) => paragraph.units),
  });
  for (const paragraph of paragraphs) {
    if (paragraph.heading) {
      const mentionsInHeading = codeMentions(paragraph.units[0] ?? '');
      if (mentionsInHeading.length) {
        carriedCode = mentionsInHeading[mentionsInHeading.length - 1]!.code;
        codeFresh = true;
      }
      scanned.push({ paragraph, own: [], refs: [] });
      continue;
    }
    const own: Own[] = [];
    for (const [unitIndex, unit] of paragraph.units.entries()) {
      const mentionsInUnit = codeMentions(unit);
      const refs = refsIn(unit, { code: carriedCode, fresh: codeFresh, defaultCode, bound });
      if (mentionsInUnit.length) {
        carriedCode = mentionsInUnit[mentionsInUnit.length - 1]!.code;
        codeFresh = true;
      }
      own.push({
        unit,
        refs,
        row: paragraph.rows[unitIndex] === true,
        header: paragraph.headers[unitIndex] === true,
        links: paragraph.links[unitIndex] ?? [],
      });
    }
    codeFresh = false;
    scanned.push({
      paragraph,
      own,
      refs: own.filter((entry) => !entry.row).flatMap((entry) => entry.refs),
    });
  }
  interface Draft {
    kind: FactCheckClaimKind;
    text: string;
    refs: RawRef[];
    inherited: boolean;
    order: number;
    context: string;
    strength: number;
    attribution: RawAttribution | null;
  }
  const drafts: Draft[] = [];
  const seenText = new Set<string>();
  let order = 0;
  const refKey = (ref: RawRef) => `${ref.code ?? '?'}|${ref.chapter}|${ref.section ?? ''}`;
  /** The provisions cited nearest to a paragraph, within the same heading section: before it first. */
  const sectionRefs = (index: number): RawRef[] => {
    const here = scanned[index]!;
    const candidates = scanned
      .map((entry, at) => ({ entry, at }))
      .filter(
        ({ entry, at }) =>
          at !== index &&
          !entry.paragraph.heading &&
          entry.paragraph.section === here.paragraph.section &&
          entry.refs.length > 0,
      )
      .sort((a, b) => Math.abs(a.at - index) - Math.abs(b.at - index) || a.at - b.at);
    const picked: RawRef[] = [];
    const keys = new Set<string>();
    for (const { entry } of candidates) {
      for (const ref of entry.refs) {
        const key = refKey(ref);
        if (keys.has(key) || picked.length >= 3) continue;
        keys.add(key);
        picked.push(ref);
      }
      if (picked.length) break;
    }
    return picked;
  };
  for (const [paragraphIndex, { paragraph, own, refs: paragraphRefs }] of scanned.entries()) {
    if (paragraph.heading) continue;
    const context = [paragraph.under, paragraph.units.join(' ')]
      .filter(Boolean)
      .join(' — ')
      .slice(0, 700);
    // The page the paragraph's previous sentence passed on carries to the sentences after it ("The guidelines
    // allow ... They also require ..."), until a sentence cites a statute of its own.
    let carried: RawAttribution | null = null;
    // Provisions an EARLIER sentence of this paragraph cited: a page's words do not carry across a citation.
    let citedSoFar = false;
    for (const { unit, refs, row, header, links } of own) {
      const text = unit.length > 500 ? `${unit.slice(0, 497)}...` : unit;
      if (seenText.has(text)) continue;
      const attributed = attributionOf(unit, links, known) ?? ruleAttributionOf(unit, bound);
      // A header names columns, a question asks, a fragment lists, advice instructs, a pointer says where to
      // look: none of them states what a provision says, and none is sent to the judge. The page a sentence
      // names still carries on to the sentences after it.
      if (header || whyNotAClaim(unit, { row })) {
        if (attributed) carried = attributed;
        continue;
      }
      if (refs.length) {
        // A sentence that cites a section is a statute claim. A link beside the citation (a guide, a
        // source line) does not make it a report of that page; only the sentence NAMING the page does.
        const namedPage = attributionOf(unit, [], known);
        seenText.add(text);
        const keys = refs
          .filter((ref) => ref.code && ref.section)
          .map((ref) => `${ref.code}.${ref.section}`);
        const companions = companionRefsFor(keys, unit).map((ref): RawRef => ({
          code: ref.code,
          chapter: sectionChapter(ref.section),
          section: ref.section,
          via: 'companion',
        }));
        drafts.push({
          kind: 'statute',
          text,
          refs: [...refs, ...companions],
          inherited: false,
          order: order++,
          context,
          strength: 0,
          attribution: namedPage,
        });
        carried = null;
        citedSoFar = true;
        continue;
      }
      if (attributed) carried = attributed;
      const kind = sentenceKind(unit);
      const source = attributed ?? (row || citedSoFar ? null : carried);
      const strength = defaultCode === 'TX' ? uncitedRuleStrength(unit) : 0;
      const rule = statesLegalRule(unit);
      // "It defines that current value as ..." after a sentence that passed on a page: the page is still the
      // speaker. Without this the sentence took the statute the paragraph cites further down, was judged
      // against the statute, and the judge's quote of the PAGE was rejected as not in the fetched source.
      const continuesPage = Boolean(source && !attributed && PAGE_CONTINUATION.test(unit));
      if (
        source &&
        (kind === 'figure' ||
          (kind && rule) ||
          (!kind && (strength > 0 || (attributed && rule))) ||
          (continuesPage && Boolean(kind || strength > 0 || rule)))
      ) {
        seenText.add(text);
        drafts.push({
          kind: kind ?? 'uncited_rule',
          text,
          refs: [],
          inherited: false,
          order: order++,
          context,
          strength,
          attribution: source,
        });
        continue;
      }
      // A sentence takes the provisions its paragraph cites only when it speaks of the law (a duty, or at least
      // the vocabulary of tax law) and does not merely report something. A date is not a duty, and a row of a
      // timeline is never lent a neighbour's citation.
      const inheritedRefs: RawRef[] = [];
      const keys = new Set<string>();
      const mayInherit = !row && speaksOfTheLaw(unit) && (rule || !isReportedHappening(unit));
      for (const ref of mayInherit ? paragraphRefs : []) {
        const key = refKey(ref);
        if (keys.has(key) || inheritedRefs.length >= 3) continue;
        keys.add(key);
        inheritedRefs.push(ref);
      }
      if (kind && inheritedRefs.length) {
        seenText.add(text);
        drafts.push({
          kind,
          text,
          refs: inheritedRefs,
          inherited: true,
          order: order++,
          context,
          strength: 0,
          attribution: null,
        });
        continue;
      }
      if (defaultCode === 'TX') {
        const ruleStrength = strength || (kind && rule && !isQuestion(unit) ? 1 : 0);
        const authority = ruleStrength ? authorityRefsFor(unit) : [];
        if (authority.length) {
          seenText.add(text);
          drafts.push({
            kind: 'uncited_rule',
            text,
            refs: authority.map((ref): RawRef => ({
              code: ref.code,
              chapter: sectionChapter(ref.section),
              section: ref.section,
              via: 'authority',
            })),
            inherited: false,
            order: order++,
            context,
            strength: ruleStrength,
            attribution: null,
          });
          continue;
        }
      }
      if (!kind) continue;
      seenText.add(text);
      if (rule) {
        const nearby = !row && statesStrictLegalRule(unit) ? sectionRefs(paragraphIndex) : [];
        drafts.push({
          kind,
          text,
          refs: nearby,
          inherited: true,
          order: order++,
          context,
          strength: 0,
          attribution: null,
        });
        continue;
      }
      // No citation of its own, none lent by its paragraph or section, and no legal rule in the sentence: a date
      // or a figure about something that happened. It is listed, never judged against a statute.
      drafts.push({
        kind: 'event',
        text,
        refs: [],
        inherited: false,
        order: order++,
        context,
        strength: 0,
        attribution: null,
      });
    }
  }
  const cited = drafts
    .filter((draft) => draft.kind !== 'uncited_rule' && draft.kind !== 'event')
    .sort((a, b) => KIND_ORDER[a.kind] - KIND_ORDER[b.kind] || a.order - b.order);
  const rules = drafts
    .filter((draft) => draft.kind === 'uncited_rule')
    .sort((a, b) => b.strength - a.strength || a.order - b.order);
  const keptCited = cited.slice(0, maxClaims);
  const keptRules = rules.slice(0, maxUncited);
  const events = drafts.filter((draft) => draft.kind === 'event');
  const keptEvents = events.slice(
    0,
    Math.min(
      MAX_EVENT_CLAIMS,
      Math.max(0, MAX_FACTCHECK_CLAIMS_STORED - keptCited.length - keptRules.length),
    ),
  );
  const kept = [...keptCited, ...keptRules, ...keptEvents].sort(
    (a, b) =>
      KIND_ORDER[a.kind] - KIND_ORDER[b.kind] ||
      (a.kind === 'uncited_rule' ? keptRules.indexOf(a) - keptRules.indexOf(b) : a.order - b.order),
  );
  const sourceIds = new Map<string, string>();
  const pageIds = new Map<string, string>();
  const claims: ExtractedClaim[] = kept.map((draft, index) => {
    const claim: ExtractedClaim = {
      id: `c${index + 1}`,
      kind: draft.kind,
      text: draft.text,
      inherited: draft.inherited,
      context: draft.context,
      refs: draft.refs.map((ref) => {
        const key = refKey(ref);
        let sourceId = sourceIds.get(key);
        if (!sourceId) {
          sourceId = `s${sourceIds.size + 1}`;
          sourceIds.set(key, sourceId);
        }
        return {
          sourceId,
          code: ref.code,
          chapter: ref.chapter,
          section: ref.section,
          citation: citationLabel(ref.code, ref.chapter, ref.section),
          url: ref.code
            ? ref.section
              ? statuteSectionUrl(ref.code, ref.chapter, ref.section)
              : statuteChapterUrl(ref.code, ref.chapter)
            : null,
          ...(ref.via ? { via: ref.via } : {}),
        };
      }),
    };
    if (draft.attribution && draft.kind !== 'event') {
      const key = normalizePageUrl(draft.attribution.url);
      let sourceId = pageIds.get(key);
      if (!sourceId) {
        sourceId = `a${pageIds.size + 1}`;
        pageIds.set(key, sourceId);
      }
      claim.attribution = {
        sourceId,
        url: draft.attribution.url,
        label: draft.attribution.label.slice(0, 200),
      };
    }
    return claim;
  });
  return {
    claims,
    omitted:
      Math.max(0, cited.length - keptCited.length) + Math.max(0, rules.length - keptRules.length),
    eventsOmitted: events.length - keptEvents.length,
    subject: articleSubject(markdown),
    scopeTerms: articleScopeTerms(markdown),
  };
}

// ── Sources ────────────────────────────────────────────────────────────────

/** One provision a claim is judged against, with what fetching it produced. */
export interface FactCheckSourceRecord extends StatuteRef {
  heading: string | null;
  /** Clean text of the section as fetched, or null when it could not be read. */
  excerpt: string | null;
  fetchedOk: boolean;
  note: string | null;
  /**
   * What was read is only PART of the section (the retained page ended inside it, or the section is longer than
   * the read keeps). What is not in a part is not known to be absent from the whole.
   */
  partial?: boolean;
}

export function uniqueSourceRefs(claims: readonly ExtractedClaim[]): StatuteRef[] {
  const bySource = new Map<string, StatuteRef>();
  for (const claim of claims)
    for (const ref of claim.refs) {
      const existing = bySource.get(ref.sourceId);
      // A provision the article cites is read before one the checker chose, even
      // when another claim also reaches it through the topic map.
      if (!existing || (existing.via && !ref.via)) bySource.set(ref.sourceId, ref);
    }
  return [...bySource.values()];
}

/** The non-statute pages claims are attributed to, one per page. */
export function uniqueAttributions(claims: readonly ExtractedClaim[]): ClaimAttribution[] {
  const byId = new Map<string, ClaimAttribution>();
  for (const claim of claims)
    if (claim.attribution && !byId.has(claim.attribution.sourceId))
      byId.set(claim.attribution.sourceId, claim.attribution);
  return [...byId.values()];
}

/** 151.359 sorts before 151.3595; the digits after the dot are a decimal sequence. */
export function compareSections(a: string, b: string): number {
  const [chapterA = '', suffixA = ''] = a.split('.');
  const [chapterB = '', suffixB = ''] = b.split('.');
  const chapterDelta = parseInt(chapterA, 10) - parseInt(chapterB, 10);
  if (chapterDelta) return chapterDelta;
  return parseFloat(`0.${suffixA}`) - parseFloat(`0.${suffixB}`);
}

export interface ChapterGroup {
  key: string;
  code: string;
  chapter: string;
  /** Section refs in ascending order. */
  sections: StatuteRef[];
  /** A claim cited the chapter as a whole. */
  chapterRef: StatuteRef | null;
}

/** De-duplicates by chapter: one chapter page serves every section cited from it. */
export function groupByChapter(refs: readonly StatuteRef[]): ChapterGroup[] {
  const groups = new Map<string, ChapterGroup>();
  for (const ref of refs) {
    if (!ref.code) continue;
    const key = `${ref.code}.${ref.chapter}`;
    let group = groups.get(key);
    if (!group) {
      group = { key, code: ref.code, chapter: ref.chapter, sections: [], chapterRef: null };
      groups.set(key, group);
    }
    if (ref.section) group.sections.push(ref);
    else group.chapterRef = ref;
  }
  for (const group of groups.values())
    group.sections.sort((a, b) => compareSections(a.section!, b.section!));
  return [...groups.values()];
}

export function stripLinks(text: string): string {
  return text
    .replace(/!\[[^\]]*\]\([^)]*\)/g, '')
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/[ \t]+/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * Every section number an article cites with a section word or sign, in a sentence that states a rule or one
 * that only points ("See Section 41.43"). Claim extraction leaves pointers, questions and headers out, so a
 * reader that wants the law in front of the reviewer must not take its list from the claims. A number that
 * is not a statute section is harmless to the caller: it asks for a heading the page does not have.
 */
export function citedSectionNumbers(markdown: string): string[] {
  const found = new Set<string>();
  for (const match of stripLinks(markdown).matchAll(SECTION_LIST))
    for (const number of match[1]!.matchAll(SECTION_NUMBER)) found.add(number[0]);
  return [...found];
}

/**
 * The text of one statute section out of a fetched chapter page. A heading
 * beats a cross-reference: the legislature's pages render each section as
 * `[Sec. 151.359. TITLE](…#151.359)` and the number recurs inside other
 * sections ("as provided by Section 151.359").
 */
export function sectionExcerpt(
  rawText: string,
  section: string,
  maxChars = FACTCHECK_SECTION_EXCERPT_CHARS,
): { heading: string; excerpt: string; truncated: boolean; reachedEnd: boolean } | null {
  const escaped = escapeRegExp(section);
  const patterns = [
    new RegExp(`\\[\\s*Sec\\.\\s*${escaped}\\.`, 'i'),
    new RegExp(`(?:^|\\n)\\s*#*\\s*Sec\\.\\s*${escaped}\\.`, 'i'),
    new RegExp(`Sec\\.\\s*${escaped}\\.`, 'i'),
  ];
  let start = -1;
  for (const pattern of patterns) {
    const match = pattern.exec(rawText);
    if (match) {
      start = match.index + (match[0].startsWith('\n') ? 1 : 0);
      break;
    }
  }
  if (start < 0) return null;
  const rest = rawText.slice(start);
  const nextHeading = /(?:\[\s*|\n\s*#*\s*)Sec\.\s*\d+[A-Za-z]?\.\d+[A-Za-z]?\./gi;
  let end = rest.length;
  for (const match of rest.matchAll(nextHeading)) {
    if (match.index! < 12) continue;
    const number = /Sec\.\s*(\d+[A-Za-z]?\.\d+[A-Za-z]?)\./i.exec(match[0])?.[1];
    if (number && number.toLowerCase() === section.toLowerCase()) continue;
    end = match.index!;
    break;
  }
  const cleaned = stripLinks(rest.slice(0, end));
  const truncated = cleaned.length > maxChars;
  const excerpt = (truncated ? cleaned.slice(0, maxChars) : cleaned).replace(
    /[\uD800-\uDBFF]$/,
    '',
  );
  // `reachedEnd`: no later `Sec.` heading closed this section, so it runs to the last character of the text it
  // was cut from. On a page that was itself cut short that is the page's end, not the section's.
  return { heading: sectionHeading(excerpt), excerpt, truncated, reachedEnd: end === rest.length };
}

/** A statute chapter as the legislature's site renders it names its sections: `Sec. 22.01.`. */
export function hasStatuteHeadings(pageText: string): boolean {
  return /\bSec\.\s*\d/.test(pageText);
}

export const STATUTE_SHELL_NOTE =
  "The legislature's page returned its site navigation instead of the statute text, so no section could be read from it.";

/**
 * "Sec. 23.011. COST METHOD OF APPRAISAL." — the legislature's upper-case
 * caption, not the first 200 characters of the section. Falls back to the
 * opening of the text when a section carries no caption.
 */
export function sectionHeading(excerpt: string): string {
  const caption =
    /^Sec\.\s*\d+[A-Za-z]?\.\d+[A-Za-z]?\.\s+([A-Z0-9][A-Z0-9 ,;:'&()/\-"]*?\.)(?=\s|$)/.exec(
      excerpt,
    );
  if (caption) return caption[0].trim();
  return excerpt.split('\n')[0]!.slice(0, 120).trim();
}

/** The opening of a chapter page, for a claim that cites the chapter as a whole. */
export function chapterExcerpt(
  rawText: string,
  maxChars = FACTCHECK_EXCERPT_CHARS,
): { heading: string; excerpt: string; truncated: boolean } | null {
  const cleaned = stripLinks(rawText);
  if (!cleaned) return null;
  const truncated = cleaned.length > maxChars;
  const excerpt = (truncated ? cleaned.slice(0, maxChars) : cleaned).replace(
    /[\uD800-\uDBFF]$/,
    '',
  );
  return { heading: excerpt.split('\n')[0]!.slice(0, 200).trim(), excerpt, truncated };
}

// ── Judgement ──────────────────────────────────────────────────────────────

/** One judgement, as the stored record and the resolver require it. The model's reply is normalised into this. */
export const FactCheckJudgementItemSchema = z.object({
  claimId: z.string().min(1).max(20),
  verdict: z.enum(FACTCHECK_JUDGE_VERDICTS),
  /** The source the quote comes from; empty when there is no quote. */
  sourceId: z.string().max(20),
  /** An exact passage from that source's excerpt; empty for unsupported / unverifiable. */
  quote: z.string().max(800),
  /**
   * A second passage, for a sentence whose rule is stated in two places (a bundled claim). Each quote is
   * checked on its own against the fetched text; one that is not in it is dropped, never accepted.
   */
  extraQuote: z.string().max(800).optional(),
  /** What the source actually says, when the claim is contradicted or misapplied; otherwise empty. */
  correction: z.string().max(800),
  /**
   * Whether the provision's scope (real vs personal property, one method of
   * appraisal, one chapter, one class of taxpayer) covers what the article
   * applies it to. Reported for every verdict that rests on a quote.
   */
  applies: z.enum(FACTCHECK_APPLIES),
  /** One or two sentences: why this verdict, and what the provision's scope is. */
  reason: z.string().max(400),
});

export const FactCheckJudgementSchema = z.object({
  judgements: z.array(FactCheckJudgementItemSchema).max(40),
});
export type FactCheckModelJudgement = z.infer<
  typeof FactCheckJudgementSchema
>['judgements'][number];

export interface FactCheckClaim {
  id: string;
  text: string;
  kind: FactCheckClaimKind;
  citation: string | null;
  verdict: FactCheckVerdict;
  /** An exact substring of the fetched source. Empty unless the verdict rests on one. */
  quote: string;
  /** A second exact substring of the fetched text, when the sentence bundles two rules the check found in two places. */
  extraQuote?: string;
  correction: string;
  sourceUrl: string | null;
  /** Why a claim is unverifiable, in a sentence a person can act on. */
  note: string | null;
  /**
   * A chapter-only citation ("Chapter 312") with no section. The opening of a chapter
   * cannot settle a sentence, so it is reported but never gates the check.
   */
  informational: boolean;
  /** Whether the provision's scope covers what the article applies it to; null when never judged. */
  applies: FactCheckApplies | null;
  /** The judge's own short reason, for a reviewer to weigh. Empty when there was none. */
  reason: string;
  /**
   * Two runs on this exact article text disagreed about this claim. Set when an earlier
   * wrong-law verdict was kept because the later run could not back its different
   * answer with a verified quote, or was overturned by one that could.
   */
  disputed?: boolean;
  /** The non-statute page this sentence passes on; the claim was judged against that page's text. */
  attribution?: { url: string; label: string };
  /**
   * The writer's own record for this sentence (an imported article's claims ledger): the page it named, and
   * whether its supporting quote was found, verbatim, in the page text Mora fetched. A quote that is not
   * found is never evidence.
   */
  ledger?: { sourceUrl: string; verified: boolean };
}

/** A non-statute page a claim is attributed to, and what fetching it produced. */
export interface FactCheckPageRecord {
  sourceId: string;
  url: string;
  label: string;
  fetchedOk: boolean;
  /** The text of the page the judge was shown, links stripped; null when it could not be read. */
  excerpt: string | null;
  note: string | null;
  /** Taken from the evidence store, not fetched by this job. */
  fromEvidence?: boolean;
  /** The judge was shown only part of the page (it was cut when retained, or windowed around the passages cited). */
  partial?: boolean;
}

/** The reason a claim attributed to a page is left unchecked when the page could not be read. */
export const SOURCE_UNREADABLE_REASON = 'source unreadable';

/** Whitespace and typography only. Never case, never words. */
function squash(text: string): string {
  return text
    .replace(/[\u00A0\u2000-\u200B\u202F]/g, ' ')
    .replace(/[’‘]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/\s+/g, ' ')
    .trim();
}

/** True only when `quote` is a contiguous passage of `excerpt`. */
export function quoteIsInExcerpt(quote: string, excerpt: string): boolean {
  const needle = squash(quote);
  if (needle.length < MIN_QUOTE_CHARS) return false;
  return squash(excerpt).includes(needle);
}

function usable(source: FactCheckSourceRecord | undefined): source is FactCheckSourceRecord & {
  excerpt: string;
} {
  return Boolean(source?.fetchedOk && source.excerpt?.trim());
}

function usablePage(page: FactCheckPageRecord | undefined): page is FactCheckPageRecord & {
  excerpt: string;
} {
  return Boolean(page?.fetchedOk && page.excerpt?.trim());
}

/** Cites law, but only at chapter level: no ref names a section. */
export function isChapterOnlyClaim(claim: Pick<ExtractedClaim, 'kind' | 'refs'>): boolean {
  return (
    claim.kind === 'statute' && claim.refs.length > 0 && claim.refs.every((ref) => !ref.section)
  );
}

/**
 * The provisions that must be readable for a claim to be judged at all. A cited
 * claim needs one of the provisions its sentence cites: a companion section
 * alone must never stand in for the section the article named. An uncited rule
 * has only the checker's own provisions, so any of them will do.
 */
function decisiveRefs(claim: ExtractedClaim): StatuteRef[] {
  if (claim.kind === 'uncited_rule') return claim.refs;
  const own = claim.refs.filter((ref) => !ref.via);
  return own.length ? own : claim.refs;
}

/** The citation a claim is reported under: its decisive provisions, joined; null when it cites none. */
export function claimCitationOf(claim: ExtractedClaim): string | null {
  return claim.refs.length
    ? decisiveRefs(claim)
        .map((ref) => ref.citation)
        .join('; ')
    : null;
}

/** Why a claim could not be sent to the judge, or null when it can be. */
export function whyUnverifiable(
  claim: ExtractedClaim,
  sources: ReadonlyMap<string, FactCheckSourceRecord>,
  pages: ReadonlyMap<string, FactCheckPageRecord> = new Map(),
): string | null {
  if (claim.attribution) {
    const page = pages.get(claim.attribution.sourceId);
    if (!usablePage(page)) {
      const why = (page as FactCheckPageRecord | undefined)?.note;
      return `The page this sentence passes on (${claim.attribution.label}) could not be read${why ? `: ${why}` : ''}, so the sentence was not checked.`;
    }
  }
  if (!claim.refs.length) {
    if (claim.attribution) return null;
    return 'No statute is cited in or near this sentence, so there is no primary source to check it against.';
  }
  const decisive = decisiveRefs(claim);
  if (decisive.some((ref) => usable(sources.get(ref.sourceId)))) return null;
  const notes = decisive
    .map((ref) => sources.get(ref.sourceId)?.note)
    .filter((note): note is string => Boolean(note));
  if (decisive.every((ref) => !ref.code))
    return 'The sentence does not say which Texas code the section belongs to, so its page cannot be found.';
  return notes[0] ?? 'The cited law could not be read.';
}

export const EVENT_CLAIM_NOTE =
  'A date or a happening, not a statement of law. No statute can support or contradict it, so it was not checked against one.';

/** The reason a claim carries when the model could not return a usable verdict for it. */
export const JUDGE_FAILED_REASON = 'judge failed';

/** A citation's own numbers (`§ 41.44(a)`, `Section 312.002`) are not figures of the claim. */
const CITATION_NUMBERS =
  /(?:§§?|\bSections?\b|\bSecs?\.|\bChapters?\b)\s*\d+[A-Za-z]?(?:\.\d+[A-Za-z]?)*(?:\([A-Za-z0-9]{1,3}\))*/gi;
const FIGURE = /\d[\d,]*(?:\.\d+)?/g;

/**
 * The numbers a passage states, normalised so `$0.805`, `0.8050` and `$ 0.805` are one figure and `1,000`
 * is `1000`. Citation numbers and subsection markers are not figures.
 */
export function figuresIn(text: string): Set<string> {
  const cleaned = text.replace(CITATION_NUMBERS, ' ').replace(/\([A-Za-z]{1,2}\)/g, ' ');
  const out = new Set<string>();
  for (const match of cleaned.matchAll(FIGURE)) {
    const value = Number(match[0].replace(/,/g, ''));
    if (Number.isFinite(value)) out.add(String(value));
  }
  return out;
}

/** A word that says one figure differs from another, followed by the figure it differs from. */
const DIFFERENCE_BEFORE_FIGURE =
  /\b(?:different(?:\s+(?:from|than))?|differs?(?:\s+from)?|rather\s+than|instead\s+of|other\s+than|contrary\s+to|not|unlike|versus|vs\.?)\s+(?:the\s+)?(?:stated\s+|claimed\s+|same\s+)?(?:(?:figure|rate|amount|number|value|percentage|threshold|deadline|period)\s+(?:of\s+)?)?(\$?\s?\d[\d,]*(?:\.\d+)?)/gi;

/**
 * A `contradicted` verdict says the law states something DIFFERENT from the claim. The judge contradicts
 * itself when its correction gives no figure the sentence does not already state AND sets a figure of the
 * sentence against itself: "a rate different from $0.805" under a sentence that says $0.805, or the same
 * figure twice ("0.805 ... not 0.805"). A correction that repeats the sentence's figure and says something
 * else is a real contradiction ("the statute sets 10 percent and does not call it non-waivable") and is kept.
 */
export function correctionRestatesClaim(claimText: string, correction: string): boolean {
  const stated = figuresIn(claimText);
  const corrected = figuresIn(correction);
  if (!stated.size || !corrected.size) return false;
  if (![...corrected].every((figure) => stated.has(figure))) return false;
  for (const match of correction.matchAll(DIFFERENCE_BEFORE_FIGURE)) {
    const [figure] = [...figuresIn(match[1]!)];
    if (figure !== undefined && stated.has(figure)) return true;
  }
  const cleaned = correction.replace(CITATION_NUMBERS, ' ').replace(/\([A-Za-z]{1,2}\)/g, ' ');
  const seen = new Map<string, number>();
  for (const match of cleaned.matchAll(FIGURE)) {
    const figure = String(Number(match[0].replace(/,/g, '')));
    seen.set(figure, (seen.get(figure) ?? 0) + 1);
  }
  return [...seen].some(([figure, times]) => times >= 2 && stated.has(figure));
}

/** A unit written twice: "$0.805 cents per $100", "$5 dollars". The symbol already says what the word says. */
const REDUNDANT_UNIT = /(\$\s?\d[\d,]*(?:\.\d+)?)\s+(?:cents?|dollars?|USD)\b/gi;
const MONEY_OR_PERCENT = /\$\s?\d[\d,]*(?:\.\d+)?|\d[\d,]*(?:\.\d+)?\s?%/g;

/**
 * Currency and unit words, spelled one way: `$0.805 cents` and `$5 dollars` are `$0.805` and `$5`; `5 dollars` is
 * `$5`; `5 percent`, `5 per cent` and `5 %` are `5%`. Words that change the amount ("80.5 cents" against "$0.805")
 * are NOT touched: that is a different figure, and equating it is for a person to do.
 */
export function normaliseUnitWords(text: string): string {
  return text
    .replace(REDUNDANT_UNIT, '$1')
    .replace(/\b(\d[\d,]*(?:\.\d+)?)\s+dollars?\b/gi, '$$$1')
    .replace(/(\d[\d,]*(?:\.\d+)?)\s?(?:percent\b|per\s?cent\b|pct\b\.?|%)/gi, '$1%')
    .replace(/\$\s+(\d)/g, '$$$1');
}

function amountsIn(text: string): Set<string> {
  const out = new Set<string>();
  for (const match of normaliseUnitWords(text).matchAll(MONEY_OR_PERCENT)) {
    const value = Number(match[0].replace(/[$,%\s]/g, ''));
    if (Number.isFinite(value)) out.add(`${match[0].includes('%') ? '%' : '$'}${value}`);
  }
  return out;
}

export const UNIT_WORDING_NOTE =
  'The source writes the unit twice (for example "$0.805 cents per $100"). The figures match, so this is not a contradiction.';

/**
 * A `contradicted` verdict whose only difference from the sentence is a unit word the page writes redundantly: the
 * article says "$0.805 per $100", the county's budget says "$0.805 cents per $100". The judge reads the extra word as
 * a different rate. It is a contradiction only if a figure differs: every amount the sentence states is on the page,
 * and the judge's own correction states no figure the sentence does not. Anything else stays contradicted.
 */
export function contradictionIsUnitWording(
  claimText: string,
  quote: string,
  correction: string,
): boolean {
  const writesUnitTwice = [claimText, quote, correction].some((text) => {
    REDUNDANT_UNIT.lastIndex = 0;
    return REDUNDANT_UNIT.test(text);
  });
  if (!writesUnitTwice) return false;
  const stated = amountsIn(claimText);
  if (!stated.size) return false;
  const onPage = amountsIn(quote);
  if (![...stated].every((amount) => onPage.has(amount))) return false;
  const corrected = figuresIn(normaliseUnitWords(correction));
  const claimed = figuresIn(normaliseUnitWords(claimText));
  return corrected.size > 0 && [...corrected].every((figure) => claimed.has(figure));
}

export const RECONCILED_NOTE =
  'The checker called this unverifiable, but its own reason says the rule is stated and quotes it. The quote was found word for word in the fetched text, so it is accepted as support.';

/** The judge's reason says the text states, confirms or contains the rule. */
const REASON_CONFIRMS =
  /\b(?:confirm\w*|verif(?:y|ies|ied)|stat(?:es|ed)|provid(?:es|ed)|says?|said|sets? out|contains?|matches|consistent with|supports?|found)\b/i;
/**
 * What a reason says when it reports a gap rather than a finding: "does not contain", "cuts off before",
 * "but no supplied provision", "cannot be verified". A negation inside the rule being confirmed ("was not
 * established by a written agreement") is the rule's own wording, not a gap, and is not matched.
 */
const REASON_DENIES = new RegExp(
  [
    "\\b(?:does|do|did|is|are|was|were|could|can|will)(?:\\s+not|n't)\\s+(?:\\w+\\s+)?(?:contain|state|address|say|include|settle|define|provide|show|mention|specify|list|set out|support|verify|confirm|appear|reach|cover)\\b",
    '\\bcannot\\b',
    '\\bunable\\b',
    '\\bnot (?:in|found|present|stated|supplied|provided|included|shown)\\b',
    '\\b(?:but|however|although|though)\\s+(?:no|not|neither|nothing|the|it|that)\\b',
    '\\b(?:cuts? off|cut off|truncat\\w*|missing|absent|silent|nothing|neither|no supplied|no provision|no text)\\b',
    '\\bfails? to\\b',
  ].join('|'),
  'i',
);

function quotedPassages(text: string): string[] {
  const found: string[] = [];
  for (const match of text.matchAll(/"([^"]{12,})"|\u201C([^\u201D]{12,})\u201D/g))
    found.push((match[1] ?? match[2])!);
  return found;
}

/**
 * `unverifiable` reconciled with the reason that came with it. Pure and conservative: the verdict changes
 * only when the reason affirms the rule (and denies nothing) AND a quote the judge gave, or one inside the
 * reason, is a contiguous passage of one of the claim's fetched texts.
 */
export function rescueConfirmedUnverifiable(
  judgement: FactCheckModelJudgement,
  candidates: ReadonlyArray<{ sourceId: string; excerpt: string }>,
): FactCheckModelJudgement | null {
  if (judgement.verdict !== 'unverifiable') return null;
  const reason = judgement.reason.trim();
  if (!reason || !REASON_CONFIRMS.test(reason) || REASON_DENIES.test(reason)) return null;
  for (const passage of [judgement.quote, ...quotedPassages(reason)]) {
    const origin = candidates.find((source) => quoteIsInExcerpt(passage, source.excerpt));
    if (origin)
      return {
        ...judgement,
        verdict: 'supported',
        quote: squash(passage),
        sourceId: origin.sourceId,
        // The prompt makes an `unverifiable` answer carry applies "unclear"; that was the label's doing.
        applies: judgement.applies === 'unclear' ? 'applies' : judgement.applies,
      };
  }
  return null;
}

const EVIDENCE_VERDICTS: readonly FactCheckVerdict[] = ['supported', 'contradicted', 'misapplied'];

/**
 * Turn the model's judgements into the claims the report keeps. Everything the
 * model said is checked here: a missing judgement, a quote the source does not
 * contain, or a verdict with no quote at all becomes `unverifiable`, never a
 * silent pass. The verdict is only as strong as the evidence it can quote.
 *
 * Applicability is settled here as well, not by the prompt: a `supported` that
 * the judge itself flags `misapplied` is `misapplied`, and one flagged `unclear`
 * is `unverifiable`. Neither can pass.
 *
 * A claim attributed to a non-statute page is judged against that page only (its quote must be a substring
 * of the page's text, and the page's text is the only thing that can settle it). When the page could not be
 * read the claim is `unverifiable` with the reason "source unreadable". Every `unverifiable` claim carries a
 * reason a person can read.
 */
export function resolveFactCheckClaims(input: {
  claims: readonly ExtractedClaim[];
  sources: readonly FactCheckSourceRecord[];
  judgements: readonly FactCheckModelJudgement[];
  pages?: readonly FactCheckPageRecord[];
  /** Claims whose batch the model could not answer even after a repair: `unverifiable`, reason "judge failed". */
  judgeFailed?: ReadonlySet<string>;
}): FactCheckClaim[] {
  const sources = new Map<string, FactCheckSourceRecord>(
    input.sources.map((source) => [source.sourceId, source] as const),
  );
  const pages = new Map<string, FactCheckPageRecord>(
    (input.pages ?? []).map((page) => [page.sourceId, page] as const),
  );
  const byClaim = new Map<string, FactCheckModelJudgement>(
    input.judgements.map((judgement) => [judgement.claimId, judgement] as const),
  );
  return input.claims.map((claim): FactCheckClaim => {
    const resolved = resolveOneClaim(
      claim,
      sources,
      pages,
      byClaim.get(claim.id),
      input.judgeFailed?.has(claim.id) === true,
    );
    return resolved.verdict === 'unverifiable' && !resolved.reason.trim()
      ? {
          ...resolved,
          reason: resolved.note?.trim() || 'The fetched text does not settle this claim.',
        }
      : resolved;
  });
}

/**
 * A sentence that says a text is SILENT: "§ 403.602 does not define ...", "neither A nor B says anything
 * about ...". A silence cannot be quoted, so such a sentence is never `supported`; and the provision "not stating"
 * a silence is not a finding that the sentence is wrong. Without one rule the same class got `unsupported`
 * (topic 7084e35d) and `unverifiable` (topic fc38c408) in one run.
 */
const ASSERTS_ABSENCE =
  /\b(?:does|do|did)\s+not\s+(?:define|say|state|mention|address|name|specify|list|contain|include|provide|speak)\b|\bsays?\s+nothing\b|\bno\s+(?:mention|provision|definition|reference|language)\b|\bnowhere\b|\bsilent\b|\bnot\s+(?:defined|stated|mentioned|addressed|specified)\b|\bneither\b[\s\S]{0,160}?\bnor\b/i;

export function assertsAnAbsence(sentence: string): boolean {
  return ASSERTS_ABSENCE.test(sentence);
}

/** The judge's own words resting a "different" on what the text does NOT say. */
const ABSENCE_IN_REASON =
  /\b(?:does|do|did)\s+not\s+(?:require|state|specify|mention|provide|say|include|contain|address|list|set|define|impose|authori[sz]e)\b|\bno\s+mention\b|\bnot\s+(?:stated|specified|mentioned|required|found|present)\b|\bnowhere\b|\bsilent\b|\bnothing\s+(?:in|about)\b|\babsent\b/i;

/**
 * A `contradicted` verdict whose ground is an ABSENCE: the correction and reason say what the text does not
 * state and put no figure against the sentence's own. In a part of a source that is not a contradiction; the
 * missing words may sit in the part that was not read (topic fc38c408: "does not require subtracting 20%",
 * read from the first part of an adoption order).
 */
export function contradictionRestsOnAbsence(
  claimText: string,
  judgement: { correction: string; reason: string },
): boolean {
  if (!ABSENCE_IN_REASON.test(`${judgement.correction} ${judgement.reason}`)) return false;
  const stated = figuresIn(claimText);
  const corrected = figuresIn(judgement.correction);
  return [...corrected].every((figure) => stated.has(figure));
}

export const ABSENCE_IN_PART_NOTE =
  'The checker judged this from only part of the source, and what it found wrong is something the part does not say. A silence in a part is not a contradiction. Read the whole source.';
export const SILENCE_NOTE =
  'This sentence says a text is silent on something. A silence cannot be quoted, so the check can neither confirm nor refute it. Read the cited text.';

function resolveOneClaim(
  claim: ExtractedClaim,
  sources: ReadonlyMap<string, FactCheckSourceRecord>,
  pages: ReadonlyMap<string, FactCheckPageRecord>,
  given: FactCheckModelJudgement | undefined,
  judgeFailed = false,
): FactCheckClaim {
  const attributedPage = claim.attribution ? pages.get(claim.attribution.sourceId) : undefined;
  const citation = claimCitationOf(claim);
  const firstUrl = claim.refs.length
    ? decisiveRefs(claim)
        .map((ref) => sources.get(ref.sourceId)?.url ?? ref.url)
        .find(Boolean)
    : claim.attribution?.url;
  const base = {
    id: claim.id,
    text: claim.text,
    kind: claim.kind,
    citation,
    quote: '',
    correction: '',
    sourceUrl: firstUrl ?? null,
    informational: claim.kind === 'event' ? false : isChapterOnlyClaim(claim),
    applies: null,
    reason: '',
    ...(claim.attribution
      ? { attribution: { url: claim.attribution.url, label: claim.attribution.label } }
      : {}),
  };
  if (claim.kind === 'event')
    return { ...base, verdict: 'not_applicable', note: EVENT_CLAIM_NOTE, sourceUrl: null };
  const blocked = whyUnverifiable(claim, sources, pages);
  if (blocked) {
    const unreadable = Boolean(claim.attribution && !usablePage(attributedPage));
    return {
      ...base,
      verdict: 'unverifiable',
      reason: unreadable ? SOURCE_UNREADABLE_REASON : blocked,
      note: blocked,
    };
  }
  if (!given)
    return judgeFailed
      ? {
          ...base,
          verdict: 'unverifiable',
          reason: JUDGE_FAILED_REASON,
          note: 'The checker could not return a usable verdict for this claim (judge failed). Check the claims again.',
        }
      : {
          ...base,
          verdict: 'unverifiable',
          note: 'The checker returned no verdict for this claim.',
        };
  const reason = given.reason.trim();
  const candidates: Array<{ sourceId: string; url: string; excerpt: string; partial: boolean }> = [
    ...claim.refs
      .map((ref) => sources.get(ref.sourceId))
      .filter((source): source is FactCheckSourceRecord & { excerpt: string } => usable(source))
      .map((source) => ({
        sourceId: source.sourceId,
        url: source.url ?? '',
        excerpt: source.excerpt,
        partial: source.partial === true,
      })),
    ...(claim.attribution && usablePage(attributedPage)
      ? [
          {
            sourceId: attributedPage.sourceId,
            url: attributedPage.url,
            excerpt: attributedPage.excerpt,
            partial: attributedPage.partial === true,
          },
        ]
      : []),
  ];
  const readOnlyInPart = candidates.some((source) => source.partial);
  // The judge sometimes writes `unverifiable` beside a reason that says the rule is stated and quotes it. When
  // a quote it gives (or one quoted inside its reason) is found, word for word, in the fetched text, the
  // quote is the evidence and the label is the part that was wrong. Without a verified quote it stays.
  const rescued = rescueConfirmedUnverifiable(given, candidates);
  const judgement = rescued ?? given;
  // A claim attributed to a page and citing no statute is a statement about that page: the scope of a
  // provision is not in question, so applicability is not judged.
  const pageOnly = Boolean(claim.attribution) && claim.refs.length === 0;
  // What is not in a PART of a source is not known to be absent from it: a contradiction or an "it does not
  // say" resting on an absence is unverifiable, never a finding.
  if (
    readOnlyInPart &&
    ((judgement.verdict === 'contradicted' && contradictionRestsOnAbsence(claim.text, judgement)) ||
      judgement.verdict === 'unsupported')
  )
    return { ...base, verdict: 'unverifiable', reason, note: ABSENCE_IN_PART_NOTE };
  if (EVIDENCE_VERDICTS.includes(judgement.verdict)) {
    const named = candidates.find((source) => source.sourceId === judgement.sourceId);
    const origin = [named, ...candidates].find(
      (source) => source && quoteIsInExcerpt(judgement.quote, source.excerpt),
    );
    if (!origin)
      return {
        ...base,
        verdict: 'unverifiable',
        reason,
        note: 'The checker quoted text that is not in the fetched source, so its verdict was not accepted.',
      };
    const quote = squash(judgement.quote);
    // A second passage counts only when it too is in the fetched text. One that is not is dropped.
    const extra = squash(judgement.extraQuote ?? '');
    const second =
      extra && candidates.some((source) => quoteIsInExcerpt(extra, source.excerpt)) ? extra : '';
    const evidence = {
      ...base,
      quote,
      sourceUrl: origin.url || base.sourceUrl,
      reason,
    };
    // A unit written twice by the page ("$0.805 cents per $100") is the page's wording, not a different figure.
    const unitWording =
      judgement.verdict === 'contradicted' &&
      contradictionIsUnitWording(claim.text, judgement.quote, judgement.correction);
    const verdict = unitWording ? 'supported' : judgement.verdict;
    if (verdict === 'contradicted' && correctionRestatesClaim(claim.text, judgement.correction))
      return {
        ...base,
        verdict: 'unverifiable',
        reason:
          'The checker called this contradicted, but its own correction restates the figure the sentence states.',
        note: 'The checker contradicted itself: its correction gives the same figure as the sentence, so no contradiction was shown.',
      };
    if (verdict === 'contradicted')
      return {
        ...evidence,
        verdict: 'contradicted',
        correction: judgement.correction.trim(),
        applies: pageOnly ? 'applies' : judgement.applies,
        note: null,
      };
    if (!pageOnly && (verdict === 'misapplied' || judgement.applies === 'misapplied'))
      return {
        ...evidence,
        verdict: 'misapplied',
        correction: judgement.correction.trim(),
        applies: 'misapplied',
        note: 'The provision was read, but its scope does not cover what the article applies it to.',
      };
    if (pageOnly && verdict === 'misapplied')
      return {
        ...base,
        verdict: 'unverifiable',
        reason,
        note: 'The checker could not say whether the page states this.',
      };
    if (!pageOnly && judgement.applies === 'unclear')
      return {
        ...evidence,
        verdict: 'unverifiable',
        applies: 'unclear',
        note: 'The provision states this, but it is unclear whether its scope covers what the article applies it to.',
      };
    return {
      ...evidence,
      verdict: 'supported',
      applies: 'applies',
      ...(second ? { extraQuote: second } : {}),
      note: unitWording ? UNIT_WORDING_NOTE : rescued ? RECONCILED_NOTE : null,
    };
  }
  if (judgement.verdict === 'unsupported') {
    // A sentence that says a text is silent is not "not stated" by that text: the same class, one verdict.
    if (assertsAnAbsence(claim.text))
      return { ...base, verdict: 'unverifiable', reason, note: SILENCE_NOTE };
    // For an uncited rule the provisions were the checker's guess, not the
    // article's own citation: that they do not state it is not a finding.
    if (claim.kind === 'uncited_rule' && !claim.attribution)
      return {
        ...base,
        verdict: 'unverifiable',
        reason,
        sourceUrl: candidates[0]?.url || base.sourceUrl,
        note: 'This sentence cites nothing. The provisions the checker looked up for it do not settle it.',
      };
    return {
      ...base,
      verdict: 'unsupported',
      reason,
      sourceUrl: candidates[0]?.url || base.sourceUrl,
      note: pageOnly
        ? 'The page was read and does not state this.'
        : 'The cited provision was read and does not state this.',
    };
  }
  return {
    ...base,
    verdict: 'unverifiable',
    reason,
    note: 'The fetched text does not settle this claim either way.',
  };
}

// ── The gate ───────────────────────────────────────────────────────────────

export interface FactCheckCounts {
  /** Claims that were judged. Date-and-event statements (`notApplicable`) are not among them. */
  total: number;
  /** Dates and happenings that state no legal rule: listed, never counted, never gating. */
  notApplicable: number;
  supported: number;
  contradicted: number;
  unsupported: number;
  unverifiable: number;
  /** Provision read, but its scope does not reach what the article applies it to. */
  misapplied: number;
  /** Statutory claims that gate the check: section-level citations only. */
  statutory: number;
  statutorySupported: number;
  /** Chapter-only citations, reported for information and never gating. */
  informational: number;
  /** Legal rules the article states without citing anything. */
  uncitedRules: number;
}

/**
 * A claim that can fail or hold up the check: a statute cited down to a section, or a figure or deadline
 * (a legal rule stated in a sentence of its own) that takes a section-level citation from its paragraph or
 * section. Both are the article asserting what a provision says, so both ask for a person when the law as
 * fetched does not settle them: one verdict mix, one status, whichever way the citation reached the
 * sentence. A claim attributed to a non-statute page is never a gate claim (see `isAttributedMismatch`).
 */
export function isGateClaim(
  claim: Pick<FactCheckClaim, 'kind' | 'informational'> &
    Partial<Pick<FactCheckClaim, 'citation' | 'attribution'>>,
): boolean {
  if (claim.informational || claim.attribution) return false;
  if (claim.kind === 'statute') return true;
  return (
    (claim.kind === 'figure' || claim.kind === 'deadline') &&
    typeof claim.citation === 'string' &&
    claim.citation.includes('§')
  );
}

/**
 * A claim attributed to a non-statute page that the page's own text does not bear out. It never fails the
 * check (the founder's rule: only wrong LAW fails), but it asks for a person. A page that could not be
 * read is a limitation of the check, listed with its reason, not a mismatch.
 */
export function isAttributedMismatch(
  claim: Pick<FactCheckClaim, 'verdict'> & Partial<Pick<FactCheckClaim, 'attribution'>>,
): boolean {
  return (
    Boolean(claim.attribution) &&
    (claim.verdict === 'contradicted' ||
      claim.verdict === 'unsupported' ||
      claim.verdict === 'misapplied')
  );
}

/**
 * An uncited legal rule gates only when it is positively wrong: the provisions
 * looked up for it contradict it, or do not reach what the article applies it to.
 * Its provisions were chosen by the checker, so "could not be verified" is not a
 * failure of the article.
 */
export function isUncitedRuleFailure(
  claim: Pick<FactCheckClaim, 'kind' | 'verdict'> & Partial<Pick<FactCheckClaim, 'attribution'>>,
): boolean {
  return (
    claim.kind === 'uncited_rule' &&
    !claim.attribution &&
    (claim.verdict === 'contradicted' || claim.verdict === 'misapplied')
  );
}

export function countFactCheckClaims(claims: readonly FactCheckClaim[]): FactCheckCounts {
  const count = (verdict: FactCheckVerdict) =>
    claims.filter((claim) => claim.verdict === verdict).length;
  const statutory = claims.filter(isGateClaim);
  return {
    total: claims.filter((claim) => claim.verdict !== 'not_applicable').length,
    notApplicable: count('not_applicable'),
    supported: count('supported'),
    contradicted: count('contradicted'),
    unsupported: count('unsupported'),
    unverifiable: count('unverifiable'),
    misapplied: count('misapplied'),
    statutory: statutory.length,
    statutorySupported: statutory.filter((claim) => claim.verdict === 'supported').length,
    informational: claims.filter(
      (claim) =>
        (claim.kind === 'statute' || claim.kind === 'figure' || claim.kind === 'deadline') &&
        claim.informational,
    ).length,
    uncitedRules: claims.filter((claim) => claim.kind === 'uncited_rule').length,
  };
}

export type FactCheckStatus = 'passed' | 'needs_review' | 'failed' | 'nothing_to_check';

/** A section-level claim the law contradicts, or whose provision does not reach what the article applies it to. */
function misstatesTheLaw(claim: Pick<FactCheckClaim, 'verdict'>): boolean {
  return claim.verdict === 'contradicted' || claim.verdict === 'misapplied';
}

/**
 * How many judged claims a `passed` check may leave unchecked (`unverifiable`, never a gate claim, which must
 * be supported). A pass says the article's claims were checked; a check that could not settle five of
 * fourteen sentences (three of them on pages that could not be read) did not check it. At most this many,
 * and at most this share of the judged claims, may be unchecked; one more and the status is `needs_review`.
 */
export const FACTCHECK_PASS_MAX_UNCHECKED = 2;
export const FACTCHECK_PASS_MAX_UNCHECKED_SHARE = 0.2;

/** Whether `unchecked` of `judged` claims is few enough for a pass. Zero judged claims tolerate none. */
export function uncheckedWithinPassTolerance(unchecked: number, judged: number): boolean {
  return (
    unchecked <=
    Math.min(FACTCHECK_PASS_MAX_UNCHECKED, Math.floor(judged * FACTCHECK_PASS_MAX_UNCHECKED_SHARE))
  );
}

/**
 * `nothing_to_check` — no claim cites a section, so no verdict could gate: the
 * article cites no statute, or only whole chapters. It is NOT a pass, and it is
 * not a failure either; the record says what happened rather than announcing a
 * verification that never took place.
 *
 * `passed` — every section-level statutory claim was checked against fetched law
 * and found supported by a quotable passage that applies to what the article
 * says it applies to, no page-attributed claim is contradicted or unstated, AND
 * the claims the check could not settle are within `uncheckedWithinPassTolerance`
 * (at most 2, at most 20% of the judged claims; chapter-only citations and
 * dates-and-events are not judged claims).
 *
 * `failed` — WRONG LAW ONLY: a section-level claim is contradicted or
 * misapplied, or an uncited legal rule is contradicted or misapplied by the
 * provisions looked up for it.
 *
 * `needs_review` — nothing is wrong that we can show, but the check did not
 * settle the article: a section-level claim is not supported (`unsupported` or
 * `unverifiable`), or too many other claims could not be checked. A person has
 * to look; the check has neither passed nor caught an error.
 *
 * Chapter-only citations, figures and deadlines that carry no section citation of
 * their own are reported but do not gate; they count only toward the tolerance.
 */
export function factCheckStatus(claims: readonly FactCheckClaim[]): FactCheckStatus {
  if (claims.some(isUncitedRuleFailure)) return 'failed';
  const gate = claims.filter(isGateClaim);
  if (gate.some(misstatesTheLaw)) return 'failed';
  const mismatch = claims.some(isAttributedMismatch);
  if (gate.length === 0) return mismatch ? 'needs_review' : 'nothing_to_check';
  const judged = claims.filter((claim) => claim.verdict !== 'not_applicable');
  const unchecked = judged.filter(
    (claim) => claim.verdict === 'unverifiable' && !claim.informational,
  );
  return gate.every((claim) => claim.verdict === 'supported') &&
    !mismatch &&
    uncheckedWithinPassTolerance(unchecked.length, judged.length)
    ? 'passed'
    : 'needs_review';
}

/**
 * The status of a record that carries no claims to re-derive it from (a stored
 * record whose claim list is empty), read off its counts: a contradicted or
 * misapplied claim fails; a stored pass passes only while its unverifiable claims
 * are within the same tolerance; no claim, or no section-level claim, is nothing
 * to check; anything else asks for a person.
 */
export function factCheckStatusFromCounts(
  counts: Pick<FactCheckCounts, 'total' | 'contradicted' | 'misapplied'> &
    Partial<Pick<FactCheckCounts, 'statutory' | 'unverifiable'>>,
  passed: boolean,
): FactCheckStatus {
  if (counts.contradicted + (counts.misapplied ?? 0) > 0) return 'failed';
  if (passed)
    return uncheckedWithinPassTolerance(counts.unverifiable ?? 0, counts.total)
      ? 'passed'
      : 'needs_review';
  if (counts.total === 0 || counts.statutory === 0) return 'nothing_to_check';
  return 'needs_review';
}

/** `passed` is only ever true for the `passed` status; `needs_review` and `nothing_to_check` stay false. */
export function factCheckPassed(claims: readonly FactCheckClaim[]): boolean {
  return factCheckStatus(claims) === 'passed';
}

// ── The record ─────────────────────────────────────────────────────────────

/** What was read from one page, kept so a reviewer can see why a verdict came out as it did. */
export interface FactCheckSourceRead {
  /** The section looked for; null for a whole-chapter read. */
  section: string | null;
  /** The caption found on the page, or null when the section was not there. */
  heading: string | null;
  /** The full length of the excerpt the judge was shown. */
  chars: number;
  /** The first ~1,500 characters of it. May be shortened further to keep the record small. */
  excerpt: string;
  /** Why this was read: the article cited it, or the checker chose it. */
  origin: 'cited' | 'authority' | 'companion';
}

export interface FactCheckReportSource {
  url: string;
  code: string | null;
  chapter: string;
  sections: string[];
  fetchedOk: boolean;
  note: string | null;
  reads?: FactCheckSourceRead[];
}

export interface FactCheckReport {
  jobId?: string;
  contentHash: string;
  checkedAt: string;
  sources: FactCheckReportSource[];
  claims: FactCheckClaim[];
  counts: FactCheckCounts;
  passed: boolean;
  status: FactCheckStatus;
  limitations: string[];
}

const encoder = new TextEncoder();

/** What `finish_article_factcheck` will store, measured the way a person measures a record. */
export function factCheckReportSize(report: Pick<FactCheckReport, 'sources' | 'claims'>): number {
  return encoder.encode(JSON.stringify(report)).length;
}

/**
 * Keeps the stored record under `maxChars`. Only the excerpts shown under "What
 * was read" give way, and they shrink evenly; claims, verdicts and quotes never
 * do. When even short excerpts will not fit they are dropped, with the heading
 * and the length of what was read left in place.
 */
export function fitFactCheckReportSize(
  report: FactCheckReport,
  maxChars: number = FACTCHECK_RECORD_MAX_CHARS,
): FactCheckReport {
  if (factCheckReportSize(report) <= maxChars) return report;
  const reads = report.sources.flatMap((source) => source.reads ?? []);
  const total = reads.reduce((sum, read) => sum + read.excerpt.length, 0);
  const withLimit = (limit: number): FactCheckReport => ({
    ...report,
    sources: report.sources.map((source) => ({
      ...source,
      ...(source.reads
        ? {
            reads: source.reads.map((read) => ({
              ...read,
              excerpt:
                limit > 0 ? read.excerpt.slice(0, limit).replace(/[\uD800-\uDBFF]$/, '') : '',
            })),
          }
        : {}),
    })),
    limitations: [
      ...report.limitations,
      'The passages shown under "What was read" were shortened to keep this record small. The check itself used the full text.',
    ],
  });
  if (!reads.length || total === 0) return report;
  const overBy = factCheckReportSize(report) - maxChars;
  let limit = Math.floor((total - overBy - 400) / reads.length);
  let fitted = withLimit(Math.max(0, limit));
  while (limit > 0 && factCheckReportSize(fitted) > maxChars) {
    limit = Math.floor(limit * 0.8) - 20;
    fitted = withLimit(Math.max(0, limit));
  }
  return limit < 200 ? withLimit(0) : fitted;
}

export function buildFactCheckReport(input: {
  contentHash: string;
  checkedAt: string;
  sources: FactCheckReportSource[];
  claims: FactCheckClaim[];
  limitations: string[];
}): FactCheckReport {
  return fitFactCheckReportSize({
    contentHash: input.contentHash,
    checkedAt: input.checkedAt,
    sources: input.sources,
    claims: input.claims,
    counts: countFactCheckClaims(input.claims),
    passed: factCheckPassed(input.claims),
    status: factCheckStatus(input.claims),
    limitations: input.limitations,
  });
}

const ClaimSchema = z.object({
  id: z.string(),
  text: z.string(),
  kind: z.enum(['statute', 'figure', 'deadline', 'uncited_rule', 'event']),
  citation: z.string().nullable(),
  verdict: z.enum(FACTCHECK_VERDICTS),
  quote: z.string(),
  extraQuote: z.string().optional(),
  correction: z.string(),
  sourceUrl: z.string().nullable(),
  note: z.string().nullable().optional(),
  informational: z.boolean().optional(),
  applies: z.enum(FACTCHECK_APPLIES).nullable().optional(),
  reason: z.string().optional(),
  disputed: z.boolean().optional(),
  attribution: z.object({ url: z.string(), label: z.string() }).optional(),
  ledger: z.object({ sourceUrl: z.string(), verified: z.boolean() }).optional(),
});

const ReadSchema = z.object({
  section: z.string().nullable(),
  heading: z.string().nullable(),
  chars: z.number(),
  excerpt: z.string(),
  origin: z.enum(['cited', 'authority', 'companion']).optional(),
});

export const FactCheckReportSchema = z.object({
  jobId: z.string().optional(),
  contentHash: z.string().regex(/^[0-9a-f]{64}$/),
  checkedAt: z.string(),
  sources: z.array(
    z.object({
      url: z.string(),
      code: z.string().nullable().optional(),
      chapter: z.string(),
      sections: z.array(z.string()).optional(),
      fetchedOk: z.boolean(),
      note: z.string().nullable().optional(),
      reads: z.array(ReadSchema).optional(),
    }),
  ),
  claims: z.array(ClaimSchema),
  counts: z.object({
    total: z.number(),
    supported: z.number(),
    contradicted: z.number(),
    unsupported: z.number(),
    unverifiable: z.number(),
    misapplied: z.number().optional(),
    statutory: z.number().optional(),
    statutorySupported: z.number().optional(),
    informational: z.number().optional(),
    uncitedRules: z.number().optional(),
    notApplicable: z.number().optional(),
  }),
  passed: z.boolean(),
  status: z.enum(['passed', 'needs_review', 'failed', 'nothing_to_check']).optional(),
  limitations: z.array(z.string()).optional(),
});

export interface StoredFactCheck {
  report: FactCheckReport;
  /** The record describes the article exactly as it stands. An edit makes it historical. */
  isCurrent: boolean;
}

/**
 * The fact-check stored on a document output, read back and never recomputed.
 * `passed` is re-derived from the claims here, so a stored `true` beside a
 * contradicted claim cannot survive being read.
 */
export function readFactCheck(
  documentOutput: Record<string, unknown> | null | undefined,
  currentContentHash: string | null | undefined,
): StoredFactCheck | null {
  const parsed = FactCheckReportSchema.safeParse(documentOutput?.factCheck);
  if (!parsed.success) return null;
  const raw = parsed.data;
  const claims: FactCheckClaim[] = raw.claims.map((claim) => ({
    id: claim.id,
    text: claim.text,
    kind: claim.kind,
    citation: claim.citation ?? null,
    verdict: claim.verdict,
    quote: claim.quote,
    ...(claim.extraQuote ? { extraQuote: claim.extraQuote } : {}),
    correction: claim.correction,
    sourceUrl: claim.sourceUrl ?? null,
    note: claim.note ?? null,
    informational: claim.informational ?? false,
    applies: claim.applies ?? null,
    reason:
      claim.reason?.trim() ||
      (claim.verdict === 'unverifiable'
        ? claim.note?.trim() || 'The fetched text does not settle this claim.'
        : ''),
    ...(claim.disputed ? { disputed: true } : {}),
    ...(claim.attribution ? { attribution: claim.attribution } : {}),
    ...(claim.ledger ? { ledger: claim.ledger } : {}),
  }));
  const report: FactCheckReport = {
    ...(raw.jobId ? { jobId: raw.jobId } : {}),
    contentHash: raw.contentHash,
    checkedAt: raw.checkedAt,
    sources: raw.sources.map((source) => ({
      url: source.url,
      code: source.code ?? null,
      chapter: source.chapter,
      sections: source.sections ?? [],
      fetchedOk: source.fetchedOk,
      note: source.note ?? null,
      reads: (source.reads ?? []).map((read) => ({
        section: read.section ?? null,
        heading: read.heading ?? null,
        chars: read.chars,
        excerpt: read.excerpt,
        origin: read.origin ?? 'cited',
      })),
    })),
    claims,
    counts: countFactCheckClaims(claims),
    passed: factCheckPassed(claims),
    status: factCheckStatus(claims),
    limitations: raw.limitations ?? [],
  };
  // A record with counts but no claim list has nothing to re-derive from; trust its counts, never its flag.
  if (!claims.length && raw.counts.total > 0) {
    const counts: FactCheckCounts = {
      total: raw.counts.total,
      notApplicable: raw.counts.notApplicable ?? 0,
      supported: raw.counts.supported,
      contradicted: raw.counts.contradicted,
      unsupported: raw.counts.unsupported,
      unverifiable: raw.counts.unverifiable,
      misapplied: raw.counts.misapplied ?? 0,
      statutory: raw.counts.statutory ?? 0,
      statutorySupported: raw.counts.statutorySupported ?? 0,
      informational: raw.counts.informational ?? 0,
      uncitedRules: raw.counts.uncitedRules ?? 0,
    };
    const status = factCheckStatusFromCounts(
      { ...counts, statutory: raw.counts.statutory },
      raw.passed,
    );
    report.counts = counts;
    report.status = status;
    report.passed = status === 'passed';
  }
  return {
    report,
    isCurrent: Boolean(currentContentHash && report.contentHash === currentContentHash),
  };
}

/** What the progress ring needs from a stored fact-check. */
export interface ArticleFactCheckSummary {
  isCurrent: boolean;
  passed: boolean;
  status: FactCheckStatus;
  contradicted: number;
  unsupported: number;
  unverifiable: number;
  statutory: number;
  /** Provisions read whose scope does not reach what the article applies them to. */
  misapplied?: number;
}

export function summariseFactCheck(stored: StoredFactCheck | null): ArticleFactCheckSummary | null {
  if (!stored) return null;
  const { counts, passed, status } = stored.report;
  return {
    isCurrent: stored.isCurrent,
    passed,
    status,
    contradicted: counts.contradicted,
    unsupported: counts.unsupported,
    unverifiable: counts.unverifiable,
    statutory: counts.statutory,
    misapplied: counts.misapplied,
  };
}

export const FACTCHECK_RELEASE_REASONS = {
  unchecked: 'Check claims against sources before handing this off.',
  failed: 'Fix the claims that misstate the law before handing this off.',
  stale: 'The article changed since its claims were checked — check them again.',
} as const;

export type FactCheckReleaseState = 'clear' | 'unchecked' | 'stale' | 'failed';

export type ReleaseFactCheck = Pick<ArticleFactCheckSummary, 'isCurrent' | 'status'>;

/**
 * Where the claim check stands for a NEW hand-off. `clear` only for a check on
 * this exact version whose status is `passed`, `needs_review` or
 * `nothing_to_check`; a missing check, a check of an earlier version and a
 * `failed` check all block. The release gate and every badge/filter that mirrors
 * it read this one function.
 */
export function factCheckReleaseState(
  factCheck: ReleaseFactCheck | null | undefined,
  enabled: boolean = FACTCHECK_RELEASE_GATE_ENABLED,
): FactCheckReleaseState {
  if (!enabled) return 'clear';
  if (!factCheck) return 'unchecked';
  if (!factCheck.isCurrent) return 'stale';
  return factCheck.status === 'failed' ? 'failed' : 'clear';
}

/** The reason hand-off should wait on the claim check, or null. */
export function factCheckReleaseBlocker(
  factCheck: ReleaseFactCheck | null | undefined,
  enabled: boolean = FACTCHECK_RELEASE_GATE_ENABLED,
): string | null {
  const state = factCheckReleaseState(factCheck, enabled);
  return state === 'clear' ? null : FACTCHECK_RELEASE_REASONS[state];
}
